import { describeUploadFile, transferResumableFile } from './resumableUpload';
import axios from 'axios';
import { subscribeProgressTransport, ProcessingProgressScope, ProgressMessage, ProgressConnectionState } from './progressTransport';
import type { StudioSearchCatalog, StudioSearchPage } from '../features/studio-search/types';
import {
  buildIdempotencyHeaders,
  createStreamRequestError,
  generateIdempotencyKey,
  isRetryableStreamRequestError,
  parseStreamRetryAfterMs,
  resolveIdempotencyKey,
} from './idempotency';
import { selectSearchResultMetadata } from '../utils/searchMetadata';
import { triggerBrowserDownload } from '../utils/download';
import {
  assertProtectedTransportBoundary,
  captureProtectedTransportBoundary,
  createAuthBoundaryUnsettledError,
  createProtectedObjectUrl,
  releaseProtectedObjectUrl,
  rethrowProtectedTransportRequestError,
} from './protectedTransportBoundary';
import { prepareAffiliateBootstrapRequestConfig } from './toltAffiliate';
import {
  notifyProcessingUploadIntent,
  ProcessingActivityRequest,
  ProcessingActivityResponse,
} from './processingActivity';
import {
  exceedsMediaUploadSizeLimit,
  MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE,
} from '../utils/mediaUploadPolicy';
export type { ProcessingProgressScope, ProgressMessage, ProgressConnectionState } from './progressTransport';

// Always use full URL to avoid proxy issues
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v2';
const REALTIME_API_BASE_URL = (
  process.env.REACT_APP_REALTIME_API_URL || API_BASE_URL
).replace(/\/+$/, '');
const realtimeRequestUrl = (path: string): string => (
  REALTIME_API_BASE_URL === API_BASE_URL
    ? path
    : `${REALTIME_API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
);

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

const SEARCH_REQUEST_TIMEOUT_MS = 2 * 60 * 1000;
const UPLOAD_REQUEST_TIMEOUT_MS = 15 * 60 * 1000;
// Google ingress reserves X-Google-* headers, so use an application-owned name.
const GOOGLE_DRIVE_ACCESS_TOKEN_HEADER = 'X-Drive-Access-Token';
const GOOGLE_DRIVE_TOKEN_REDACTION_HEADERS = [
  GOOGLE_DRIVE_ACCESS_TOKEN_HEADER,
  // Redaction only: older transport errors may still contain the previous name.
  'X-Google-Drive-Access-Token',
];
const uploadIdempotencyKeys = new WeakMap<File, Map<string, string>>();

const removeHeaderCaseInsensitively = (headers: any, headerName: string): void => {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete(headerName);
  }
  if (typeof headers !== 'object') return;
  Object.keys(headers).forEach((key) => {
    if (key.toLowerCase() === headerName.toLowerCase()) {
      delete headers[key];
    }
  });
};

const scrubGoogleDriveTokenFromTransport = (transport: any): void => {
  GOOGLE_DRIVE_TOKEN_REDACTION_HEADERS.forEach((headerName) => {
    removeHeaderCaseInsensitively(transport?.config?.headers, headerName);
    removeHeaderCaseInsensitively(transport?.response?.config?.headers, headerName);
    removeHeaderCaseInsensitively(transport?.request?.headers, headerName);
  });
};

const uploadRequestScope = (
  file: File,
  title: string,
  indexId: string | null,
): string => JSON.stringify([
  file.name,
  file.size,
  file.type,
  file.lastModified,
  title.trim(),
  indexId,
]);

const retainUploadIdempotencyKey = (
  file: File,
  requestScope: string,
  providedKey?: string,
): string => {
  const normalizedProvided = typeof providedKey === 'string'
    ? providedKey.trim()
    : '';
  if (normalizedProvided) {
    return normalizedProvided;
  }
  let keysForFile = uploadIdempotencyKeys.get(file);
  if (!keysForFile) {
    keysForFile = new Map<string, string>();
    uploadIdempotencyKeys.set(file, keysForFile);
  }
  const retained = keysForFile.get(requestScope);
  if (retained) {
    return retained;
  }
  const created = generateIdempotencyKey('media-upload');
  keysForFile.set(requestScope, created);
  return created;
};

const readConfig = (params: Record<string, any> = {}) => ({ params });

// Function to get Firebase ID token
let getTokenFunction: (() => Promise<string | null>) | null = null;

export const setTokenFunction = (fn: (() => Promise<string | null>) | null) => {
  getTokenFunction = fn;
};

export const AUTH_TRANSPORT_UNAVAILABLE_ERROR_CODE = 'auth_transport_unavailable';

const createAuthTransportUnavailableError = (): Error & {
  code: string;
  retryable: boolean;
} => Object.assign(
  new Error('Authentication is temporarily unavailable. Please retry this request.'),
  {
    code: AUTH_TRANSPORT_UNAVAILABLE_ERROR_CODE,
    retryable: true,
  },
);

const AUTH_IGNORED_BROWSER_API_ROUTES = new Set([
  'GET /health',
  'GET /chat/health',
]);

const OPTIONAL_AUTH_BROWSER_API_ROUTES = new Set([
  'GET /plans',
  'GET /billing/pricing',
  'GET /webhooks/events',
  'GET /usage/metric-types',
  'GET /mcp/status',
  'GET /mcp/tools',
]);

const getApiRequestPath = (url: unknown): string => {
  const rawUrl = typeof url === 'string' ? url.trim() : '';
  if (!rawUrl) {
    return '';
  }
  if (!/^https?:\/\//i.test(rawUrl)) {
    const path = rawUrl.split(/[?#]/, 1)[0] || '';
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }
  try {
    const parsedPath = new URL(rawUrl).pathname.replace(/\/+$/, '') || '/';
    const apiBasePath = new URL(API_BASE_URL).pathname.replace(/\/+$/, '');
    if (
      apiBasePath
      && apiBasePath !== '/'
      && (parsedPath === apiBasePath || parsedPath.startsWith(`${apiBasePath}/`))
    ) {
      return parsedPath.slice(apiBasePath.length) || '/';
    }
    return parsedPath;
  } catch {
    return '';
  }
};

const getBrowserApiRoute = (method: unknown, url: unknown): string => (
  `${typeof method === 'string' ? method.toUpperCase() : 'GET'} ${getApiRequestPath(url)}`
);

const removeAmbientAuthHeaders = (
  headers: Record<string, any>,
): void => {
  if (typeof headers.delete === 'function') {
    headers.delete(['Authorization', 'X-API-Key']);
  }
  delete headers.Authorization;
  delete headers.authorization;
  delete headers['X-API-Key'];
  delete headers['x-api-key'];
};

const requireFirebaseTokenProvider = (): (() => Promise<string | null>) => {
  if (!getTokenFunction) {
    throw createAuthTransportUnavailableError();
  }
  return getTokenFunction;
};

const requireNonEmptyFirebaseBearerToken = (
  tokenValue: string | null,
): string => {
  const token = tokenValue?.trim();
  if (!token) {
    throw createAuthTransportUnavailableError();
  }
  return token;
};

export const EMAIL_VERIFICATION_REQUIRED_ERROR_CODE = 'authorization_email_verification_required';

let emailVerificationRequiredHandler: ((error: any) => void | Promise<void>) | null = null;

export const setEmailVerificationRequiredHandler = (
  fn: ((error: any) => void | Promise<void>) | null,
) => {
  emailVerificationRequiredHandler = fn;
};

export const isEmailVerificationRequiredError = (error: any): boolean => {
  return (
    error?.response?.status === 403 &&
    error?.response?.data?.error?.code === EMAIL_VERIFICATION_REQUIRED_ERROR_CODE
  );
};

export const normalizeUploadIndexId = (indexId: string | null | undefined): string | null => {
  const normalized = typeof indexId === 'string' ? indexId.trim() : '';
  if (!normalized || normalized.toLowerCase() === 'playground') {
    return null;
  }
  return normalized;
};

export const getApiErrorMessage = (
  error: any,
  fallbackMessage: string,
): string => {
  const structuredError = error?.response?.data?.error;
  const structuredCode = typeof structuredError?.code === 'string' ? structuredError.code : '';
  if (structuredCode === 'validation_prompt_run_target_empty') {
    return 'This index has no media ready for extraction. Add media in Index, then try again.';
  }
  const structuredDetails = structuredError?.details || {};
  const resourceName = typeof structuredDetails.resource === 'string'
    ? structuredDetails.resource.replaceAll('_', ' ')
    : 'resource';
  if (structuredCode === 'resource_quota_exceeded') {
    const limit = Number(structuredDetails.limit);
    const used = Number(structuredDetails.used);
    const reserved = Number(structuredDetails.reserved);
    const usageSummary = Number.isFinite(limit) && Number.isFinite(used)
      ? ` (${used} used${Number.isFinite(reserved) && reserved > 0 ? `, ${reserved} pending` : ''} of ${limit})`
      : '';
    return `Your account has reached its ${resourceName} limit${usageSummary}. Delete unused resources or upgrade before creating more.`;
  }
  if (structuredCode === 'resource_rate_quota_exceeded') {
    const resetTime = typeof structuredDetails.reset_time === 'string' && structuredDetails.reset_time
      ? ` It resets at ${structuredDetails.reset_time}.`
      : ' Please try again after the limit resets.';
    return `Your account has reached its temporary ${resourceName} limit.${resetTime}`;
  }
  if (structuredCode === 'llm_daily_budget_exceeded') {
    return 'You have reached today’s AI helper usage limit. Try again after the daily limit resets.';
  }
  if (structuredCode === 'llm_budget_guard_open') {
    return 'AI helpers are temporarily unavailable because the shared daily safety budget has been reached. Other product features remain available.';
  }
  if (structuredCode === 'llm_billing_guard_unavailable') {
    return 'AI helper billing is temporarily unavailable, so no generation was started. Please try again later.';
  }
  const structuredMessage = error?.response?.data?.error?.message;
  if (typeof structuredMessage === 'string' && structuredMessage.trim()) {
    return structuredMessage.trim();
  }
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }
  if (detail && typeof detail?.message === 'string' && detail.message.trim()) {
    return detail.message.trim();
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const firstDetail = detail[0];
    if (typeof firstDetail === 'string' && firstDetail.trim()) {
      return firstDetail.trim();
    }
    if (firstDetail && typeof firstDetail?.msg === 'string' && firstDetail.msg.trim()) {
      return firstDetail.msg.trim();
    }
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }
  return fallbackMessage;
};

const readBlobAsText = async (blob: Blob): Promise<string> => {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

export const normalizeApiErrorResponse = async (error: any): Promise<any> => {
  const response = error?.response;
  const data = response?.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) {
    return error;
  }
  const contentType = String(
    response?.headers?.['content-type']
      || response?.headers?.['Content-Type']
      || data.type
      || '',
  ).toLowerCase();
  if (!contentType.includes('json')) {
    return error;
  }
  try {
    const parsed = JSON.parse(await readBlobAsText(data));
    if (parsed && typeof parsed === 'object') {
      response.data = parsed;
    }
  } catch {
    // Preserve the original transport error when a malformed body cannot be
    // normalized; callers still receive its HTTP status and fallback message.
  }
  return error;
};

const readFetchErrorPayload = async (
  response: Response,
): Promise<{ data: unknown; message: string }> => {
  const responseBody = await response.text().catch(() => '');
  if (!responseBody) {
    return { data: null, message: '' };
  }
  try {
    const data = JSON.parse(responseBody);
    const structuredMessage = data?.error?.message
      || data?.detail?.message
      || data?.message;
    return {
      data,
      message: typeof structuredMessage === 'string'
        ? structuredMessage
        : '',
    };
  } catch {
    return { data: null, message: responseBody };
  }
};

const readFetchRetryAfterMs = (response: Response): number | null => (
  parseStreamRetryAfterMs(response.headers?.get?.('Retry-After'))
);


// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const route = getBrowserApiRoute(config.method, config.url);
    // Health surfaces intentionally carry no ambient credential.
    if (AUTH_IGNORED_BROWSER_API_ROUTES.has(route)) {
      removeAmbientAuthHeaders(config.headers);
      return config;
    }
    // Public catalog/status surfaces may use a valid identity for canonical
    // plan-aware rate admission, but auth unavailability must not make public
    // data unreachable. This mirrors the backend optional-auth route policy.
    if (OPTIONAL_AUTH_BROWSER_API_ROUTES.has(route)) {
      try {
        const token = getTokenFunction
          ? (await getTokenFunction())?.trim()
          : '';
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          removeAmbientAuthHeaders(config.headers);
        }
      } catch {
        removeAmbientAuthHeaders(config.headers);
      }
      return config;
    }
    const token = requireNonEmptyFirebaseBearerToken(
      await requireFirebaseTokenProvider()(),
    );
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    scrubGoogleDriveTokenFromTransport(response);
    return response;
  },
  async (error) => {
    // Strip request-scoped capabilities before even the shared response-body
    // normalizer can observe the Axios transport object.
    scrubGoogleDriveTokenFromTransport(error);
    error = await normalizeApiErrorResponse(error);
    // Defend against a future normalizer returning a replacement transport
    // object that retains its request configuration.
    scrubGoogleDriveTokenFromTransport(error);
    if (getBrowserApiRoute(error.config?.method, error.config?.url) === 'POST /users/me/processing-activity') {
      // Readiness is advisory; its failures must not change auth UI or block uploads.
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      // Handle unauthorized access
      console.error('Unauthorized access - token may be invalid');
      // The auth context will handle logout if needed
    }
    if (isEmailVerificationRequiredError(error) && emailVerificationRequiredHandler) {
      try {
        await emailVerificationRequiredHandler(error);
      } catch (handlerError) {
        console.error('Email verification handler failed:', handlerError);
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface Index {
  index_id: string;
  name: string;
  user_id: string;
  created_at: string;
  video_count?: number;
  description?: string | null;
  sort_order?: number | null;
  is_default?: boolean;
  preview?: {
    video_id: string;
    title: string;
    thumbnail_gcs_uri?: string | null;
    gif_gcs_uri?: string | null;
  } | null;
}

export interface RealtimeProject {
  project_id: string;
  name: string;
  created_at: string;
}
export type RealtimeFeedStatus = 'starting' | 'connecting' | 'live' | 'reconnecting' | 'stopping' | 'stopped' | 'failed' | 'needs_credentials';
export interface RealtimeEvent {
  event_id: string;
  project_id: string;
  feed_id: string;
  runtime_id: string;
  sequence: number;
  captured_start_at: string;
  captured_end_at: string;
  media_duration_seconds: number;
  configuration_id: string;
  title: string;
  description: string;
  tags: string[];
  alert_level: RealtimeAlertLevel;
  alert_reason: string;
  extracted_metadata: Record<string, unknown>;
  filter_matched: boolean;
  clip_url: string;
  thumbnail_url: string | null;
  storage_state: 'pending' | 'uploading' | 'stored' | 'failed';
  indexing_state: 'pending' | 'ready' | 'failed';
  thumbnail_state: 'pending' | 'ready' | 'failed';
  score?: number;
  test_video_id?: string | null;
  media_start_seconds?: number | null;
  media_end_seconds?: number | null;
}
export interface RealtimeFeed {
  feed_id: string;
  project_id: string;
  name: string;
  source_url: string;
  source_label: string;
  engine_id: string;
  engine_snapshot: Prompt;
  configuration_id: string;
  requires_credentials: boolean;
  desired_state: 'running' | 'stopped';
  status: RealtimeFeedStatus;
  runtime_id: string | null;
  created_at: string;
  started_at: string | null;
  live_at: string | null;
  stopped_at: string | null;
  retry_attempt: number;
  max_retry_attempts: number;
  next_retry_at: string | null;
  last_error: string;
  last_sequence: number;
  latest_event: RealtimeEvent | null;
  buffer_seconds: 10 | 15 | 30 | 60;
  resolution: 'low' | 'high';
  model: string;
  playlist_url: string | null;
  input_mode?: 'live' | 'video_test';
  test_video_id?: string | null;
  test_video_name?: string | null;
  test_video_url?: string | null;
  test_duration_seconds?: number | null;
}
export interface RealtimeRecording {
  recording_id: string;
  name: string;
  duration_seconds: number;
  size_bytes: number;
  created_at: string;
  playback_url: string;
}
export interface StartRealtimeFeedRequest {
  username?: string;
  password?: string;
  test_video_id?: string;
}
export interface CreateRealtimeFeedRequest {
  name: string;
  source_url: string;
  engine_id: string;
  buffer_seconds: 10 | 15 | 30 | 60;
  resolution: 'low' | 'high';
  username?: string;
  password?: string;
}
export interface UpdateRealtimeFeedRequest {
  name?: string;
  source_url?: string;
  engine_id?: string;
  engine_snapshot?: Prompt;
}
export interface RealtimeEventPage { events: RealtimeEvent[]; next_cursor: string | null; }
export interface RealtimeEventScope {
  feed_ids?: string[];
  alert_levels?: RealtimeAlertLevel[];
  from_at?: string;
  to_at?: string;
  matching_only?: boolean;
}
export interface RealtimeSearchRequest extends RealtimeEventScope {
  mode: 'semantic' | 'conditions';
  query?: string;
  conditions?: FilterCondition[];
  page_size?: number;
  cursor?: string | null;
}
export interface RealtimeAlertStatsRequest extends Pick<RealtimeEventScope, 'feed_ids' | 'from_at' | 'to_at' | 'matching_only'> {
  cursor?: string | null;
}
export interface RealtimeAlertStatsPage {
  counts: Record<RealtimeAlertLevel, number>;
  total: number;
  next_cursor: string | null;
}
export interface RealtimeFieldDefinition {
  path: string;
  label: string;
  type: FilterCondition['type'];
  item_type?: 'string' | 'integer' | 'number' | 'boolean';
}
export interface RealtimeStreamEvent {
  type: string;
  payload: RealtimeFeed | RealtimeEvent | Record<string, any>;
}

export type IndexDeletionStatus = 'draining' | 'deleting' | 'deleted';

export interface IndexDeletionResponse {
  index_id: string;
  deletion_id: string;
  status: IndexDeletionStatus;
  retry_after_seconds?: number | null;
}

export type VideoDeletionStatus = 'draining' | 'deleting' | 'deleted';

export interface VideoDeletionResponse {
  video_id: string;
  deletion_id: string;
  status: VideoDeletionStatus;
  retry_after_seconds?: number | null;
}

export type MarkerColor = 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'gray';
export type MarkerEntityType = 'video' | 'segment' | 'metadata_field' | 'prompt_run';

export interface MarkerInfo {
  marker_id: string | null;
  color: MarkerColor | null;
  note: string | null;
  updated_at: string | null;
}

export interface MarkerRecord {
  marker_id: string;
  entity_type: MarkerEntityType;
  target_key: string;
  color: MarkerColor | null;
  note: string | null;
  index_id?: string | null;
  run_id?: string | null;
  video_id?: string | null;
  segment_id?: string | null;
  field_key?: string | null;
  segment_start_time?: number | null;
  segment_end_time?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface UpsertMarkerRequest {
  entity_type: MarkerEntityType;
  color: MarkerColor | null;
  note: string | null;
  index_id?: string;
  run_id?: string;
  video_id?: string;
  segment_id?: string;
  field_key?: string;
  segment_start_time?: number;
  segment_end_time?: number;
}

export interface Video {
  video_id: string;
  title: string;
  video_uri: string;
  status: string;
  processing_status?: PromptRunProcessingStatus[];
  created_at: string;
  updated_at: string;
  metadata_keys?: string[] | null;
  media_type?: string;
  duration_seconds?: number | null;
  thumbnail_gcs_uri?: string | null;
  thumbnail_uri?: string | null;
  thumbnail_available?: boolean;
  /** Canonical animated tile preview, when the selected video projection has one. */
  gif_gcs_uri?: string | null;
  gif_uri?: string | null;
  gif_available?: boolean;
  marker?: MarkerInfo;
}

interface GoogleDriveUploadRequest {
  fileId: string;
  resourceKey?: string | null;
  title?: string | null;
  indexId?: string | null;
  googleAccessToken: string;
  idempotencyKey: string;
  expectedUserId: string;
}

export interface VideoWithDetails extends Video {
  gif_data?: string;
  first_thumbnail?: string;
}

export interface VideoStatus {
  video_id: string;
  status: string;
  processing_status?: PromptRunProcessingStatus[];
}

export interface SegmentResponse {
  segment_id: string;
  video_id: string;
  start_time: number;
  end_time: number;
  gcs_uri?: string;
  playback_source_uri?: string | null;
  playback_start_time?: number | null;
  playback_end_time?: number | null;
  thumbnail_gcs_uri?: string;
  gif_gcs_uri?: string;
  thumbnail_uri?: string;
  gif_uri?: string;
  media_type?: 'video' | 'audio' | 'image' | null;
  processed: boolean;
  processing_failed: boolean;
  segment_status?: 'pending' | 'processing' | 'successful' | 'failed';
  failure_stage?: string | null;
  failure_message?: string | null;
  attempt_id?: string | null;
  status_source?: string | null;
  metadata?: Record<string, any>;
  metadata_text?: string;
  created_at?: string;
  processed_at?: string;
  error_message?: string;
  processing_warning?: string;
  thumbnail_data?: string;
  thumbnail_available: boolean;
  gif_data?: string;
  gif_available: boolean;
  from_run_id?: string;
  marker?: MarkerInfo;
  metadata_markers?: Record<string, MarkerInfo>;
}

export interface VideoSegmentsResponse {
  video_id: string;
  run_id?: string | null;
  thumbnail_gcs_uri?: string | null;
  thumbnail_uri?: string | null;
  thumbnail_available?: boolean;
  gif_gcs_uri?: string | null;
  gif_uri?: string | null;
  gif_available?: boolean;
  segments: SegmentResponse[];
  has_more?: boolean;
  next_cursor?: string | null;
}

export interface BatchVideoSegmentsTarget {
  video_id: string;
  run_id?: string | null;
  segment_ids?: string[];
  cursor?: string | null;
  limit?: number;
}

export type SearchResultType = 'segment' | 'video';
export type MatchType = 'both' | 'text_only' | 'image_only';

export interface MatchedFieldInstance {
  field_path: string;
  field_display_label?: string | null;
  field_instance_key: string;
  field_instance_path: string;
  field_instance_display_label?: string | null;
  score: number;
  value_preview?: string;
}

export interface SearchResult {
  result_type: SearchResultType;
  result_id: string;
  video_id: string;
  video_uri?: string | null;
  segment_id: string | null;
  start_time: number | null;
  end_time: number | null;
  preview_segment_id?: string | null;
  preview_start_time?: number | null;
  preview_end_time?: number | null;
  preview_segment_uri?: string | null;
  preview_thumbnail_uri?: string | null;
  preview_gif_uri?: string | null;

  // URIs
  segment_uri?: string | null;
  gcs_uri?: string | null;
  playback_source_uri?: string | null;
  playback_start_time?: number | null;
  playback_end_time?: number | null;
  thumbnail_gcs_uri?: string | null;
  gif_gcs_uri?: string | null;
  thumbnail_uri?: string;
  gif_uri?: string;
  video_thumbnail_gcs_uri?: string | null;
  video_thumbnail_uri?: string;
  video_thumbnail_available?: boolean;
  video_gif_gcs_uri?: string | null;
  video_gif_uri?: string;
  video_gif_available?: boolean;
  media_type?: 'video' | 'audio' | 'image' | null;

  // Base64 encoded media
  thumbnail_data?: string;  // Base64 encoded thumbnail image
  thumbnail_available: boolean;
  gif_data?: string;  // Base64 encoded GIF
  gif_available: boolean;

  // Content
  text_content: string;
  content_preview?: string;
  metadata_text?: string;

  // Scoring
  similarity_score: number | null;
  fused_score?: number;
  text_score?: number | null;
  image_score?: number | null;
  text_rank?: number | null;
  image_rank?: number | null;
  match_type?: MatchType;
  reranked_score?: number;

  // Full metadata from Firestore
  metadata?: {
    summary?: string;
    captions?: string;
    keywords?: string;
    [key: string]: any;  // all other LLM-extracted fields
  };
  extracted_metadata?: Record<string, any>; // For backwards compatibility

  // Per-field similarity scores (field_name -> score). Absent fields were not included in search.
  field_scores?: Record<string, number> | null;
  // Exact field-instance scores keyed by an opaque exact-instance identifier.
  field_instance_scores?: Record<string, number> | null;
  matched_field_paths?: string[] | null;
  matched_field_instances?: MatchedFieldInstance[] | null;

  // Image search specific
  matched_image_gcs_uri?: string | null;
  matched_image_uri?: string;
  matched_image_timestamp?: number | null;
  matched_image_score?: number;
  shot_timestamp?: number | null;

  // Run info
  run_id?: string;
  source_run_id?: string;
  raw_llm_response?: string;

  // Additional fields for grouping
  video_name?: string;
  prompt_run_id?: string;

  // Source tracking for multi-index search
  source_index_id?: string;

  marker?: MarkerInfo;
  extracted_metadata_markers?: Record<string, MarkerInfo>;
  sql_preview_hydration_attempted?: boolean;
  sql_row_index?: number;
}

export type PreviewGrantRequestItem =
  | {
      kind: 'video';
      video_id: string;
    }
  | {
      kind: 'segment';
      video_id: string;
      segment_id: string;
    };

export interface PreviewGrantRequest {
  items: PreviewGrantRequestItem[];
}

export interface PreviewGrantItem {
  kind: PreviewGrantRequestItem['kind'];
  video_id: string;
  segment_id: string | null;
  thumbnail_gcs_uri: string | null;
  thumbnail_uri: string | null;
  thumbnail_available: boolean;
  thumbnail_error_code: string | null;
  gif_gcs_uri: string | null;
  gif_uri: string | null;
  gif_available: boolean;
  gif_error_code: string | null;
  grant_expires_at: string | null;
}

export interface PreviewGrantResponse {
  items: PreviewGrantItem[];
}

export interface MultimodalSearchResult extends SearchResult {
  fused_score: number;
  match_type: MatchType;
}

export interface CreateIndexRequest {
  name: string;
}

export interface CreateVideoRequest {
  title: string;
  video_uri: string;
  index_id: string;
  media_type?: 'video' | 'audio' | 'image';
  source_connector_id?: string;
}

export interface SearchRequest {
  query: string;
  top_k?: number;
  search_fields?: string[] | null;
  run_ids?: string[] | null;
  index_ids?: string[] | null;
  view?: 'full' | 'gallery';
}

export type EngineKind = 'video' | 'realtime';
export type RealtimeAlertLevel = 'clean' | 'suspicious' | 'attention' | 'critical';
export interface RealtimeEngineConfig {
  alert_rubric: Record<RealtimeAlertLevel, string>;
}

export interface CreatePromptRequest {
  is_realtime?: boolean;
  realtime_config?: RealtimeEngineConfig | null;
  name: string;
  description: string;
  prompt_text: string;
  json_schema: Record<string, any>;
  video_level?: VideoLevelPromptConfig | null;
  semantic_indexing?: PromptSemanticIndexingConfig | null;
  execution_config?: PromptExecutionConfig | null;
  selection_filter?: PromptSelectionFilter | null;
  clear_video_level?: boolean;
}

export interface VideoLevelPromptConfig {
  instructions_text: string;
  json_schema: Record<string, any>;
}

export interface PromptSemanticIndexingConfig {
  disabled_segment_fields: string[];
  disabled_video_level_fields: string[];
}

export interface PromptExecutionConfig {
  video_segmentation_type: 'fixed' | 'content_aware';
  audio_segmentation_type: 'fixed' | 'content_aware';
  video_segment_duration: number | null;
  audio_segment_duration: number | null;
  processing_model: string | null;
  enable_image_embedding: boolean;
}

export interface PromptSelectionCondition {
  field: string;
  operator: string;
  value?: unknown;
  type: 'string' | 'integer' | 'number' | 'boolean' | 'array';
  item_type?: 'string' | 'integer' | 'number' | 'boolean';
}

export interface PromptSelectionFilterGroup {
  logic: 'and' | 'or';
  conditions: PromptSelectionCondition[];
}

export interface PromptSelectionFilter {
  segment?: PromptSelectionFilterGroup;
  video?: PromptSelectionFilterGroup;
  level_logic: 'and' | 'or';
}

export interface Prompt {
  is_realtime?: boolean;
  realtime_config?: RealtimeEngineConfig | null;
  prompt_id: string;
  user_id: string;
  name: string;
  description: string;
  prompt_text: string;
  json_schema: Record<string, any>;
  video_level?: VideoLevelPromptConfig | null;
  semantic_indexing: PromptSemanticIndexingConfig;
  execution_config?: PromptExecutionConfig | null;
  selection_filter?: PromptSelectionFilter | null;
  created_at: string;
  is_active: boolean;
}

export interface PromptListResponse {
  prompts: Prompt[];
  total_count?: number;
  active_count?: number;
  default_prompt_id?: string | null;
}

export interface GeneratedPromptDraft {
  name: string;
  description: string;
  prompt_text: string;
  json_schema: Record<string, any>;
  video_level?: VideoLevelPromptConfig | null;
  semantic_indexing: PromptSemanticIndexingConfig;
  execution_config?: PromptExecutionConfig | null;
  selection_filter?: PromptSelectionFilter | null;
}

export interface WorkflowDefineResponse {
  prompt_id: string | null;
  saved: boolean;
  definition: GeneratedPromptDraft;
  prompt: Prompt | null;
}

export interface PromptRun {
  result_revisions?: ResultRevisions;
  activity_counts?: Record<string, number>;
  liveness?: ProcessingLiveness;
  summary?: { total_media: number; completed: number; partial: number; failed: number; cancelled: number; total_segments: number; completed_segments: number };
  run_id: string;
  prompt_id: string;
  prompt_name: string;
  prompt_type: "system" | "custom";
  status: "pending" | "processing" | "completed" | "failed" | "completed_with_failures" | "cancelled";
  executed_at: string;
  executed_by: string;
  run_context: Record<string, any>;
  total_videos: number;
  completed_videos: number;
  failed_videos: number;
  error_message?: string;
  video_segmentation_type?: string;
  audio_segmentation_type?: string;
  image_segmentation_type?: string;
  video_segment_duration?: number;
  audio_segment_duration?: number;
  created_new_segments?: boolean;
  processing_model?: string;
  total_video_seconds?: number;
  enable_image_embedding?: boolean;
  // Media type counts
  total_audios?: number;
  total_images?: number;
  completed_audios?: number;
  completed_images?: number;
  failed_audios?: number;
  failed_images?: number;
  partial_videos?: number;
  partial_audios?: number;
  partial_images?: number;
  cancelled_videos?: number;
  cancelled_audios?: number;
  cancelled_images?: number;
  // Segment counts
  total_segments?: number;
  completed_segments?: number;
  // Operation failure counts
  field_extraction_failures?: number;
  // Operation success flags (null=pending/N/A, true=all succeeded, false=at least one failed)
  field_extraction_succeeded?: boolean | null;
  video_level_enabled?: boolean;
  video_level_total_items?: number;
  video_level_completed_items?: number;
  video_level_failed_items?: number;
  video_level_partial_items?: number;
  billing_estimated_mt?: number;
  billing_actual_mt?: number;
  billing_status?: string | null;
  billing_error?: string | null;
  reuse_summary?: PromptRunReuseSummary | null;
  selection_summary?: PromptRunSelectionSummary | null;
  selection_filter?: PromptSelectionFilter | null;
  marker?: MarkerInfo;
}

export interface PromptRunReuseSummary {
  target_media_count: number;
  reused_media_count: number;
  fresh_media_count: number;
}

export interface PromptRunSelectionSummary {
  status: 'not_applicable' | 'pending' | 'ready' | 'failed';
  selected_media_count: number;
  selected_video_count: number;
  selected_segment_count: number;
}

export interface PromptRunWorkflowVideoResult {
  video_id: string;
  media_type: 'video' | 'audio' | 'image';
  video_matched: boolean;
  matched_segment_ids: string[];
  video?: (Partial<Video> & Pick<Video, 'video_id'> & {
    extracted_metadata?: Record<string, any>;
    metadata_text?: string;
  }) | null;
}

export interface PromptRunWorkflowSegmentResult {
  video_id: string;
  segment_id: string;
  media_type: 'video' | 'audio' | 'image';
  video_matched: boolean;
  segment?: SegmentResponse | null;
}

export type PromptRunWorkflowResult =
  | PromptRunWorkflowVideoResult
  | PromptRunWorkflowSegmentResult;


export interface PromptRunFailureOperationCounts {
  field_extraction: number;
  image_embedding: number;
  processing: number;
}

export interface PromptRunFailedSegment {
  segment_id: string;
  failed_operations: string[];
  field_extraction_error?: string | null;
  image_embedding_error?: string | null;
  failure_stage?: string | null;
  failure_message?: string | null;
  failure_code?: string | null;
  retryable?: boolean | null;
  start_time?: number | null;
  end_time?: number | null;
  projection_only?: boolean;
}

export interface PromptRunFailedVideo {
  video_id: string;
  failed_segments: number;
  operation_counts: PromptRunFailureOperationCounts;
  segments: PromptRunFailedSegment[];
}

export interface PromptRunFailedSegmentsManifest {
  run_id: string;
  status: string;
  videos_with_failures: number;
  failed_segments: number;
  operation_counts: PromptRunFailureOperationCounts;
  videos: PromptRunFailedVideo[];
}

export interface SegmentRunProcessingStatus {
  readiness?: ProcessingReadiness;
  activity?: { phase: string; started_at?: string; updated_at?: string };
  segment_id: string;
  status: string;
  start_time?: number;
  end_time?: number;
  started_at?: string;
  completed_at?: string;
  updated_at?: string;
  error_message?: string | null;
  failure_stage?: string | null;
  attempt_id?: string | null;
}

export interface ImageEnrichmentProgress {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'disabled';
  completed: number;
  total: number;
  failed: number;
}

export interface PromptRunProcessingStatus {
  result_revisions?: ResultRevisions;
  activity?: { phase: string; started_at?: string; updated_at?: string };
  activity_counts?: Record<string, number>;
  readiness?: ProcessingReadiness;
  enrichment?: { image?: ImageEnrichmentProgress };
  liveness?: ProcessingLiveness;
  run_id: string;
  prompt_id?: string | null;
  status: string;
  total_parts?: number;
  completed_parts?: number;
  failed_parts?: number;
  total_segments: number;
  pending_segments: number;
  processing_segments: number;
  successful_segments: number;
  failed_segments: number;
  created_at?: string;
  updated_at?: string;
  attempt_id?: string | null;
  video_level?: VideoLevelProcessingStatus | null;
  segments: SegmentRunProcessingStatus[];
}

export interface VideoLevelProcessingStatus {
  status: string;
  result_available: boolean;
  successful_segment_count: number;
  failed_segment_count: number;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
  error_message?: string | null;
  attempt_id?: string | null;
}

export interface ProcessingStreamEvent {
  event_id?: string;
  event_type: string;
  user_id?: string;
  run_id?: string;
  video_id?: string;
  segment_id?: string;
  index_id?: string;
  created_at?: string;
  payload: Record<string, any>;
}

export type ResultRevisions = Partial<Record<'metadata' | 'segments' | 'video_level' | 'assets' | 'text_index' | 'image_index', string | number>>;

export interface ProcessingReadiness {
  metadata_available?: boolean;
  assets_available?: boolean;
  text_search_ready?: boolean;
  image_search_ready?: boolean;
}

export interface ProcessingLiveness {
  attempt_id?: string;
  last_heartbeat_at?: string;
  lease_expires_at?: string;
}

export interface LocalUploadDescriptor {
  file_name: string;
  content_type: string;
  size_bytes: number;
  last_modified: number;
  sample_sha256: string;
}
export interface ResumableUploadSession { transport: 'resumable'; url: string; offset: number; total_bytes: number; chunk_size: number; expires_at: string }
export interface ResumableUploadResponse {
  job: ImportJob;
  upload: ResumableUploadSession | null;
}

export interface ExecutePromptRequest {
  prompt_id: string;
  target: {
    type: "index" | "videos" | "playground";
    index_id?: string;  // For type 'index' or when videos belong to an index
    video_ids?: string[];  // For type 'videos'
  };
  video_segmentation_type?: string;
  audio_segmentation_type?: string;
  video_segment_duration?: number | null;
  audio_segment_duration?: number | null;
  processing_model?: string | null;
  enable_image_embedding?: boolean;
}

export interface PromptRunEstimateRequest {
  prompt_id: string;
  target: ExecutePromptRequest["target"];
  video_segmentation_type?: string;
  audio_segmentation_type?: string;
  video_segment_duration?: number;
  audio_segment_duration?: number;
  processing_model?: string;
  enable_image_embedding?: boolean;
}

export interface VideoPromptRunResult {
  run_id: string;
  prompt_id: string;
  video_id: string;
  executed_at: string;
  status: string;
  metadata: Record<string, any>;
  metadata_text: string;
  raw_llm_response?: string | null;
  processing_warning?: string | null;
  schema_used?: string | null;
  successful_segment_count: number;
  failed_segment_count: number;
  source_fingerprint?: string | null;
  attempt_id?: string | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  segment_uri?: string | null;
  gcs_uri?: string | null;
  playback_source_uri?: string | null;
  playback_start_time?: number | null;
  playback_end_time?: number | null;
  thumbnail_uri?: string | null;
  thumbnail_gcs_uri?: string | null;
  gif_uri?: string | null;
  gif_gcs_uri?: string | null;
  thumbnail_available?: boolean;
  gif_available?: boolean;
  preview_segment_id?: string | null;
  preview_start_time?: number | null;
  preview_end_time?: number | null;
  preview_segment_uri?: string | null;
  preview_thumbnail_uri?: string | null;
  preview_gif_uri?: string | null;
  embedding_status?: string | null;
  embedding_error?: string | null;
}

export interface MetadataExport {
  export_id: string;
  export_type: "index" | "prompt_run" | "search_snapshot";
  target_id: string;
  created_at: string;
  status: "pending" | "processing" | "completed" | "failed";
  queue_status?: string;
  attempts?: number;
  max_attempts?: number;
  available_at?: string;
  started_at?: string;
  completed_at?: string;
  updated_at?: string;
  download_url?: string | null;
  file_size_bytes?: number;
  error_message?: string;
  last_error?: string;
  gcs_uri?: string;
  playback_source_uri?: string | null;
  playback_start_time?: number | null;
  playback_end_time?: number | null;
  export_params?: Record<string, any>;
  destination_type?: "download" | "connector";
  destination_connector_id?: string;
  destination_base_path?: string;
  destination_subpath?: string;
  destination_uri?: string;
}

export interface WorkflowDeliveryArtifact {
  scope: 'all' | 'matches' | 'search_results';
  state: 'waiting_for_source' | 'pending' | 'preparing' | 'ready' | 'failed' | 'not_applicable' | 'unavailable';
  export_id?: string | null;
  captured_at?: string | null;
  error_code?: string | null;
  error_message?: string | null;
}

export interface WorkflowDelivery {
  source_kind: 'prompt_run' | 'search_snapshot';
  source_id: string;
  artifacts: WorkflowDeliveryArtifact[];
}

export interface FilterCondition {
  field: string;
  operator: string;
  value: any;
  type: 'unknown' | 'string' | 'integer' | 'number' | 'boolean' | 'array';
  itemType?: 'string' | 'integer' | 'number' | 'boolean';
}

export interface FilterSearchResponse {
  results: SearchResult[];
  next_page_token?: string;
  total_shown: number;
}

export interface SqlSearchLimits {
  max_loaded_rows: number;
  default_result_limit: number;
  max_result_limit: number;
  query_timeout_seconds: number;
}

export interface SqlSearchCatalogTable {
  table_name: string;
  index_id: string;
  index_name: string;
  run_id: string;
  run_name: string;
  context_columns: string[];
  field_paths: string[];
  default_query: string;
}

export interface SqlSearchCatalogResponse {
  tables: SqlSearchCatalogTable[];
  limits: SqlSearchLimits;
  index_ids: string[];
  run_ids: string[];
}

export interface SqlSearchExecuteResponse {
  columns: string[];
  rows: Record<string, any>[];
  row_context?: Array<Record<string, any> | null>;
  row_count: number;
  truncated: boolean;
  applied_result_limit: number;
  tables: string[];
  index_ids: string[];
  run_ids: string[];
}

export interface SqlSearchGenerateResponse {
  query: string;
}

const serializeFilterConditions = (
  conditions: FilterCondition[],
): Array<Pick<FilterCondition, 'field' | 'operator' | 'type'> & { value?: FilterCondition['value'] }> => (
  conditions.map(({ field, operator, value, type, itemType }) => {
    const serialized: Pick<FilterCondition, 'field' | 'operator' | 'type'> & {
      value?: FilterCondition['value'];
    } = { field, operator, type };
    if (operator !== 'is_empty' && operator !== 'is_not_empty') {
      serialized.value = normalizeFilterConditionValue(type, operator, value, itemType);
    }
    return serialized;
  })
);

function parseFiniteNumber(value: FilterCondition['value']): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== 'string' || value.trim().length === 0) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseBoolean(value: FilterCondition['value']): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') {
    return true;
  }
  if (normalized === 'false') {
    return false;
  }
  return undefined;
}

function normalizeFilterConditionValue(
  type: FilterCondition['type'],
  operator: string,
  value: FilterCondition['value'],
  itemType?: FilterCondition['itemType'],
): FilterCondition['value'] {
  if (type === 'integer' || (type === 'array' && operator.startsWith('length_'))) {
    const parsed = parseFiniteNumber(value);
    return parsed !== undefined && Number.isInteger(parsed) ? parsed : value;
  }
  if (type === 'number') {
    return parseFiniteNumber(value) ?? value;
  }
  if (type === 'boolean') {
    return parseBoolean(value) ?? value;
  }
  if (type === 'array' && operator === 'item_equals') {
    if (itemType === 'integer') {
      const parsed = parseFiniteNumber(value);
      return parsed !== undefined && Number.isInteger(parsed) ? parsed : value;
    }
    if (itemType === 'number') {
      return parseFiniteNumber(value) ?? value;
    }
    if (itemType === 'boolean') {
      return parseBoolean(value) ?? value;
    }
  }
  return value;
}

// Pagination types for list endpoints
export interface PaginationMeta {
  limit: number;
  has_more: boolean;
  next_cursor?: string;
}

export interface SignedUrlContext {
  videoId?: string | null;
  segmentId?: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

// Webhook types
export interface Webhook {
  webhook_id: string;
  name: string;
  url: string;
  events: string[];
  index_ids: string[] | null;
  status: 'active' | 'paused' | 'disabled';
  failure_count: number;
  last_failure_at: string | null;
  last_success_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  metadata: Record<string, any> | null;
}

export interface WebhookCreateResponse extends Webhook {
  secret: string;
}

export interface WebhookDelivery {
  delivery_id: string;
  webhook_id: string;
  event_type: string;
  status: 'pending' | 'delivered' | 'failed' | 'retrying';
  attempts: number;
  last_attempt_at: string | null;
  next_retry_at: string | null;
  response_status_code: number | null;
  response_body: string | null;
  error_message: string | null;
  created_at: string | null;
  completed_at: string | null;
  payload: Record<string, any> | null;
}

export interface CreateWebhookRequest {
  name: string;
  url: string;
  events: string[];
  index_ids?: string[] | null;
  metadata?: Record<string, any> | null;
}

export interface UpdateWebhookRequest {
  name?: string;
  url?: string;
  events?: string[];
  index_ids?: string[] | null;
  status?: 'active' | 'paused';
  metadata?: Record<string, any> | null;
}

export interface TestWebhookResponse {
  success: boolean;
  status_code: number | null;
  error: string | null;
}

export interface RotateSecretResponse {
  webhook_id: string;
  new_secret: string;
}

// API Key types
export interface ApiKey {
  key_id: string;
  key_prefix: string;
  name: string;
  scopes: string[];
  created_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  expires_at: string | null;
  is_active: boolean;
}

export interface ApiKeyCreateResponse extends ApiKey {
  key: string; // Full key, only shown once at creation
}

export interface CreateApiKeyRequest {
  name: string;
  scopes?: string[];
  expires_in_days?: number;
}

export interface ApiKeyRotateResponse extends ApiKeyCreateResponse {
  // Same as create response - includes new full key
}

// MCP (Model Context Protocol) types
export type MCPPlatform = 'claude-desktop' | 'cursor' | 'custom';

export interface MCPStatusResponse {
  server_name: string;
  server_version: string;
  package_name: string;
  npm_install_command: string;
  documentation_url: string;
  tools_count: number;
  supported_platforms: string[];
}

export interface MCPConfigResponse {
  platform: string;
  config: Record<string, any>;
  instructions: string;
  config_path?: string;
  api_key_hint: string;
}

export interface MCPToolInputSchema {
  type: string;
  properties: Record<string, any>;
  required?: string[];
}

export interface MCPToolDefinition {
  name: string;
  description: string;
  inputSchema: MCPToolInputSchema;
  category?: string;
  annotations?: Record<string, any>;
  idempotencyProtected: boolean;
}

export interface MCPToolsResponse {
  total_count: number;
  tools: MCPToolDefinition[];
  categories: Record<string, string[]>;
}

export interface MCPPlaygroundRequest {
  tool_name: string;
  arguments: Record<string, any>;
}

export interface MCPPlaygroundResponse {
  tool_name: string;
  success: boolean;
  result?: Record<string, any>;
  error?: {
    code: string;
    message: string;
    details: Record<string, any>;
  };
  execution_time_ms: number;
}

export interface MCPAttachmentUploadGrant {
  url: string;
  complete_url: string;
  token: string;
  expires_at: string;
  max_files: number;
  accept: string;
}

// Chat types
export interface RequestedScope {
  index_ids: string[];
  prompt_run_ids: string[];
}

export interface AgentSessionSummary {
  session_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  title?: string | null;
  is_active: boolean;
  turn_count: number;
  last_turn_id?: string | null;
  last_turn_status?: 'processing' | 'completed' | 'failed' | null;
}

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface AgentToolTraceEntry {
  tool_name: string;
  arguments: Record<string, any>;
  result_summary: Record<string, any>;
  started_at?: string | null;
  completed_at?: string | null;
  error?: string | null;
}

export interface AgentResultSet {
  result_set_id: string;
  position?: number | null;
  label: string;
  result_level: 'segment' | 'video' | 'mixed';
  origin: 'search' | 'memory' | 'selection';
  source_query?: string | null;
  summary?: string | null;
  result_count: number;
  results: SearchResult[];
}

export interface AgentTurnStats {
  tool_calls: number;
  review_loops: number;
  evidence_result_count: number;
  selected_result_count: number;
  duration_ms?: number | null;
  model_name?: string | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  total_tokens?: number | null;
  context_input_tokens?: number | null;
}

export interface AgentTurnError {
  error_code: string;
  message: string;
  details: Record<string, any>;
}

export interface AgentTurn {
  turn_id: string;
  session_id: string;
  status: 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  user_message: AgentMessage;
  assistant_message?: AgentMessage | null;
  effective_scope: RequestedScope;
  tool_trace: AgentToolTraceEntry[];
  result_sets: AgentResultSet[];
  stats: AgentTurnStats;
  error?: AgentTurnError | null;
}

export interface AgentSessionDetail extends AgentSessionSummary {
  turns: AgentTurn[];
}

export interface CreateSessionRequest {
  title?: string;
}

export interface SendMessageRequest {
  message: string;
  scope?: RequestedScope;
  idempotency_key?: string;
}

interface ChatStreamEventBase {
  event_id?: string;
  event_seq?: number;
}

export interface ChatTurnStartedEvent extends ChatStreamEventBase {
  type: 'turn_started';
  turn_id: string;
  session_id: string;
  timestamp: string;
}

export interface ChatTurnResumedEvent extends ChatStreamEventBase {
  type: 'turn_resumed';
  turn_id: string;
  session_id: string;
  status: 'processing' | 'completed' | 'failed';
  timestamp: string;
}

export interface ChatAgentStateEvent extends ChatStreamEventBase {
  type: 'agent_state';
  turn_id: string;
  state: 'prepare_turn' | 'understand_request' | 'tool_loop' | 'review_evidence' | 'compose_response';
  message: string;
  progress: number;
  loop_index: number;
}

export interface ChatToolCallStartedEvent extends ChatStreamEventBase {
  type: 'tool_call_started';
  turn_id: string;
  tool_name: string;
  arguments: Record<string, any>;
}

export interface ChatToolCallResultEvent extends ChatStreamEventBase {
  type: 'tool_call_result';
  turn_id: string;
  tool_name: string;
  result_summary: Record<string, any>;
}

export interface ChatTurnCompletedEvent extends ChatStreamEventBase {
  type: 'turn_completed';
  turn: AgentTurn;
}

export interface ChatErrorResponse extends ChatStreamEventBase {
  type: 'error';
  error_code?: string;
  message: string;
  details?: Record<string, any>;
}

export type ChatStreamEvent =
  | ChatTurnStartedEvent
  | ChatTurnResumedEvent
  | ChatAgentStateEvent
  | ChatToolCallStartedEvent
  | ChatToolCallResultEvent
  | ChatTurnCompletedEvent
  | ChatErrorResponse;

export type ChatSession = AgentSessionSummary;
export type ChatMessage = AgentMessage;
export type ChatSegment = SearchResult;
export type SessionHistoryResponse = AgentSessionDetail;

// Cloud Connector types
export type ConnectorScope = 'import' | 'export';
export type ConnectorImportMode = 'all' | 'new_only';
export type DebounceInterval = '5m' | '10m' | '1h' | '1d';
export type AutomationRuntimeStatus = 'provisioning' | 'active' | 'scheduler_error' | 'paused';
export interface AutomationPromptPreset {
  prompt_id: string;
}

export interface Connector {
  connector_id: string;
  name: string;
  provider: 'gcs' | 's3' | 'azure';
  status:
    | 'active'
    | 'testing'
    | 'failed'
    | 'disabled'
    | 'provisioning'
    | 'deleting'
    | 'cleanup_pending';
  scopes: ConnectorScope[];
  import_mode?: ConnectorImportMode;
  export_base_path?: string;
  bucket?: string;
  region?: string;
  storage_account?: string;
  container?: string;
  gcp_project_id?: string;
  created_at?: string;
  updated_at?: string;
  last_tested_at?: string;
  last_test_result?: string;
  last_test_error?: string;
}

export interface CreateGCSConnectorRequest {
  name: string;
  bucket: string;
  gcp_project_id: string;
  scopes?: ConnectorScope[];
  import_mode?: ConnectorImportMode;
  export_base_path?: string;
}

export interface CreateS3ConnectorRequest {
  name: string;
  bucket: string;
  region: string;
  aws_access_key_id: string;
  aws_secret_access_key: string;
  scopes?: ConnectorScope[];
  import_mode?: ConnectorImportMode;
  export_base_path?: string;
}

export interface CreateAzureConnectorRequest {
  name: string;
  storage_account: string;
  container: string;
  tenant_id: string;
  client_id: string;
  client_secret: string;
  scopes?: ConnectorScope[];
  import_mode?: ConnectorImportMode;
  export_base_path?: string;
}

export interface UpdateConnectorRequest {
  import_mode?: ConnectorImportMode;
}

export interface ImportAutomationConfig {
  index_id: string;
  user_id: string;
  connector_id: string;
  connector_provider: string;
  connector_bucket: string;
  enabled: boolean;
  paused: boolean;
  source_prefix: string;
  file_pattern: string;
  recursive: boolean;
  debounce_interval: DebounceInterval | string;
  prompt_preset: AutomationPromptPreset;
  runtime_status?: AutomationRuntimeStatus | null;
  runtime_error?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  next_execution_at?: string | null;
  next_execution_error?: string | null;
}

export interface PutImportAutomationRequest {
  enabled: boolean;
  paused: boolean;
  connector_id: string;
  source_prefix: string;
  file_pattern: string;
  recursive: boolean;
  debounce_interval: DebounceInterval;
  prompt_preset: AutomationPromptPreset;
}

export interface ExportAutomationConfig {
  index_id: string;
  user_id: string;
  destination_connector_id: string;
  destination_subpath?: string | null;
  enabled: boolean;
  paused: boolean;
  runtime_status?: AutomationRuntimeStatus | null;
  runtime_error?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface PutExportAutomationRequest {
  enabled: boolean;
  paused: boolean;
  destination_connector_id: string;
  destination_subpath?: string | null;
}

export interface ExportDestinationOptions {
  destination_connector_id?: string;
  destination_subpath?: string;
  result_scope?: 'all' | 'matches';
}

export interface TestConnectionResponse {
  success: boolean;
  error_message?: string;
}

export interface CloudFile {
  path: string;
  name: string;
  size_bytes: number;
  last_modified: string;
  content_type?: string;
  extension: string;
}

export interface BrowseFilesRequest {
  prefix?: string;
  pattern?: string;
  recursive?: boolean;
}

// Import Job types
export interface ImportJobOutput {
  file_id: string;
  file_name: string;
  source_ordinal: number;
  part_index: number;
  part_count: number;
  source_start_seconds: number;
  source_end_seconds: number;
  video_id: string | null;
  title: string;
  status: string;
}
export interface ImportJobOutputsPage {
  items: ImportJobOutput[];
  next_cursor: string | null;
  total: number;
}
export interface ImportJobFile {
  file_id: string;
  file_name: string;
  source_ordinal: number;
  status: string;
  part_count: number | null;
  parts_completed: number | null;
  parts_failed: number | null;
  failure?: { error: string; error_code: string; http_status?: number | null; refresh_required: boolean } | null;
  skip_reason?: string | null;
}
export interface ImportJobFilesPage {
  items: ImportJobFile[];
  next_cursor: string | null;
  total: number;
}
export interface ImportJobExecution {
  status: 'not_requested' | 'pending' | 'submitted' | 'blocked';
  run_id?: string | null;
  error_code?: string | null;
  error?: string | null;
}

export interface ImportJobProgress {
  bytes_total?: number;
  bytes_confirmed?: number;
  parts_total?: number;
  parts_completed?: number;
  parts_failed?: number;
  total_files: number;
  imported: number;
  failed: number;
  skipped: number;
  bytes_transferred: number;
  current_file?: string;
}

export interface AttachmentFileFailure {
  file_id: string;
  file_name: string;
  error_code: string;
  error: string;
  http_status?: number | null;
  refresh_required: boolean;
}

export interface ImportFileError {
  path: string;
  error: string;
  file_id?: string | null;
  file_name?: string | null;
  error_code?: string | null;
  http_status?: number | null;
  refresh_required?: boolean;
}

export interface RetryImportJobRequest {
  files?: Array<{
    file_id: string;
    download_url: string;
    file_name?: string;
    mime_type?: string;
  }>;
}

export interface AttachmentImportJobResponse {
  phase: string;
  outputs: ImportJobOutputsPage;
  execution: ImportJobExecution;
  job_id: string;
  source_kind: 'attachments';
  status: ImportJob['status'] | 'awaiting_upload';
  run_id?: string;
  video_ids: string[];
  target_index_id?: string;
  progress: Pick<ImportJobProgress, 'total_files' | 'imported' | 'failed' | 'bytes_transferred'>;
  error_message?: string;
  failed_files_next_cursor?: string | null;
  failed_files: AttachmentFileFailure[];
  upload?: MCPAttachmentUploadGrant;
}

export interface ImportJob {
  file_previews?: Array<{ file_id: string; file_name: string; thumbnail_uri?: string | null; status?: string }>;
  liveness?: ProcessingLiveness;
  phase?: string;
  outputs?: ImportJobOutputsPage;
  execution?: ImportJobExecution;
  job_id: string;
  connector_id: string | null;
  target_index_id: string | null;
  source_kind?: string;
  trigger_source?: 'manual' | 'automation';
  prompt_preset?: AutomationPromptPreset | null;
  prompt_run_id?: string | null;
  source_prefix: string;
  file_pattern: string;
  recursive: boolean;
  import_mode_override?: ConnectorImportMode | null;
  status: 'awaiting_upload' | 'pending' | 'scanning' | 'importing' | 'completed' | 'failed' | 'cancelled';
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  progress: ImportJobProgress;
  video_ids: string[];
  failed_files_next_cursor?: string | null;
  failed_files: ImportFileError[];
}

export interface CreateImportJobRequest {
  connector_id: string;
  index_id: string;
  source_prefix?: string;
  file_pattern?: string;
  recursive?: boolean;
  import_mode_override?: ConnectorImportMode;
}

export interface SessionSegmentsResponse {
  session_id: string;
  total_segments: number;
  suggested_segments: ChatSegment[];
  bookmarked_segments: ChatSegment[];
}

// Usage Tracking types
export interface UsageMetrics {
  videos_uploaded?: number;
  videos_processed?: number;
  video_minutes?: number;
  segments_created?: number;
  search_queries?: number;
  image_search_queries?: number;
  llm_input_tokens?: number;
  llm_output_tokens?: number;
  storage_bytes?: number;
  export_count?: number;
  import_files?: number;
  transcription_minutes?: number;
  embedding_operations?: number;
  image_embedding_operations?: number;
  [key: string]: number | undefined;
}

export interface ModelUsage {
  input_tokens: number;
  output_tokens: number;
  video_minutes: number;
}

export interface AuthUsage {
  name: string;
  llm_input_tokens?: number;
  llm_output_tokens?: number;
  video_minutes?: number;
  search_queries?: number;
  videos_processed?: number;
  [key: string]: any;
}

export interface CurrentUsageResponse {
  user_id: string;
  period_start: string;
  period_end: string;
  metrics: UsageMetrics;
  model_usage: Record<string, ModelUsage>;
  auth_usage: Record<string, AuthUsage>;
  totals?: {
    total_searches: number;
    total_tokens?: number;
    total_storage_bytes?: number;
    total_videos_uploaded?: number;
    total_videos_processed?: number;
    total_segments_created?: number;
  };
}

export interface UsagePeriod {
  summary_id?: string;
  period_start: string;
  period_end: string;
  metrics: UsageMetrics;
  model_usage: Record<string, ModelUsage>;
  auth_usage: Record<string, AuthUsage>;
  totals?: Record<string, number>;
}

export interface UsageHistoryResponse {
  user_id: string;
  periods: UsagePeriod[];
  total_periods: number;
}

export interface UsageEvent {
  event_id: string;
  user_id: string;
  metric_type: string;
  value: number;
  unit: string;
  timestamp: string;
  metadata: Record<string, any> | null;
}

export interface UsageBreakdownResponse {
  user_id: string;
  period_start: string;
  period_end: string;
  breakdown: Record<string, number>;
  total_events?: number;
  totals?: {
    total_searches: number;
    total_tokens?: number;
    total_storage_bytes?: number;
    total_videos_uploaded?: number;
    total_videos_processed?: number;
    total_segments_created?: number;
  };
}

export interface MetricTypeInfo {
  type: string;
  description: string;
  unit: string;
}

// Billing Types
export interface BillingAccount {
  trial_eligible?: boolean;
  trial_started_at?: string | null;
  trial_ends_at?: string | null;
  user_id: string;
  affiliate_lead_identifier?: string | null;
  stripe_customer_id: string | null;
  plan_id: string;
  balance_mt: number;
  subscription_cycle_remaining_mt: number;
  stripe_subscription_id: string | null;
  stripe_subscription_status: string | null;
  pending_plan_id: string | null;
  pending_plan_effective_at: string | null;
  pending_plan_status: string | null;
  pending_plan_change_type: string | null;
  paid_through: string | null;
  last_payment_failure_message: string | null;
  lifetime_credits_purchased: number;
  lifetime_credits_used: number;
  auto_recharge_enabled: boolean;
  auto_recharge_threshold_mt: number | null;
  auto_recharge_amount_mt: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BillingBudgetAlertsResponse {
  monthly_budget_mt: number | null;
  alert_percents: number[];
  period_key: string;
  period_start: string;
  period_end: string;
  consumed_mt: number;
  consumed_percent: number | null;
}

export interface BillingBudgetAlertsRequest {
  monthly_budget_mt: number | null;
  alert_percents: number[];
}

export interface BillingTransaction {
  transaction_id: string;
  transaction_type: string;
  amount_mt: number;
  amount_usd: number | null;
  description: string;
  stripe_payment_intent_id: string | null;
  stripe_invoice_id: string | null;
  stripe_invoice_url: string | null;
  stripe_invoice_pdf_url: string | null;
  stripe_invoice_status: string | null;
  stripe_invoice_billing_reason: string | null;
  package_id: string | null;
  created_at: string | null;
  metadata: Record<string, any>;
}

export interface TransactionListResponse {
  transactions: BillingTransaction[];
  total_count: number;
  has_more: boolean;
}

export interface CreditPackage {
  package_id: string;
  name: string;
  description: string | null;
  mt_amount: number;
  price_usd: number;
  bonus_mt: number;
  price_per_mt: number;
  is_popular: boolean;
  is_active: boolean;
  eligible: boolean;
  eligibility_reason: string | null;
}

export interface CostEstimate {
  estimated_mt: number;
  breakdown: Record<string, any>;
  sufficient_balance: boolean;
  current_balance_mt: number;
}

export interface CheckoutSessionResponse {
  session_id: string;
  checkout_url: string;
}

export interface BillingPortalResponse {
  url: string;
}

export interface ProcessingModelOption {
  model_id: string;
  label: string;
  coefficient: number;
  description: string;
  is_default: boolean;
  is_active: boolean;
}

export interface BillingPricingConfig {
  schema_version: string;
  model_coefficients: Record<string, number>;
  processing_model_options: ProcessingModelOption[];
  media_type_coefficients: Record<string, number>;
  operation_coefficients: Record<string, Record<string, any>>;
  prompt_run_terms: Record<string, any>;
  processing_quality: Record<string, any>;
  plan_catalog: Record<string, Record<string, any>>;
  credit_purchase: Record<string, any>;
  entitlement_policy: Record<string, any>;
  billing_controls: Record<string, any>;
}

export interface UsageDeduction {
  deduction_id: string;
  operation_type: string;
  amount_mt: number;
  description: string;
  reference_type: string | null;
  reference_id: string | null;
  metadata: Record<string, any>;
  created_at: string | null;
}

export interface DeductionListResponse {
  deductions: UsageDeduction[];
  has_more: boolean;
}

// User Profile Types
export interface UserAuth {
  provider: string | null;
  providers_linked: string[];
  mfa_enabled: boolean;
}

export interface UserOnboarding {
  completed: boolean;
  completed_at: string | null;
  steps_completed: string[];
  skipped: boolean;
  flow_version: number;
}

export interface UserSettings {
  theme_mode: string;
  notification_email: boolean;
  notification_low_credits: boolean;
  notification_weekly_digest: boolean;
  marketing_opt_in: boolean;
  default_model: string;
  api_response_format: string;
}

// Public plan catalog types for in-app billing
export interface PlanLimitBucket {
  per_minute: number;
  per_hour: number;
}

export interface PlanLimits {
  general: PlanLimitBucket;
  search: PlanLimitBucket;
  processing: PlanLimitBucket;
}

export interface PlanCredits {
  mt: number | null;
  display: string;
}

export interface Plan {
  trial_period_days?: number;
  plan_id: string;
  order: number;
  name: string;
  price_display: string;
  checkout_required: boolean;
  self_serve: boolean;
  mt_price_usd: number;
  usage_discount_percent: number;
  custom_credit_price_per_mt: number | null;
  features: string[];
  limits: PlanLimits;
  credits: PlanCredits;
}

export interface SelectPlanResponse {
  plan_id: string;
  previous_plan_id: string | null;
  current_plan_id: string;
  effective_at: string;
  pending_plan_id: string | null;
  pending_plan_effective_at: string | null;
  pending_plan_status: string | null;
  message: string;
}

export interface PlanCheckoutResponse {
  trial_period_days?: number;
  plan_id: string;
  checkout_required: boolean;
  checkout_status: string;
  checkout_session_token: string | null;
  checkout_url: string | null;
  expires_at: string | null;
  pending_plan_id?: string | null;
  pending_plan_status?: string | null;
  pending_plan_effective_at?: string | null;
}

export interface PlanCheckoutStatusResponse {
  trial_started_at?: string | null;
  trial_ends_at?: string | null;
  session_id: string;
  plan_id: string | null;
  checkout_status: string;
  checkout_session_token: string | null;
  pending_plan_id: string | null;
  pending_plan_status: string | null;
  pending_plan_effective_at: string | null;
  paid_through: string | null;
  last_payment_failure_message: string | null;
  stripe_subscription_status: string | null;
}

export interface UserProfile {
  user_id: string;
  email: string;
  email_verified: boolean;
  display_name: string | null;
  company_name: string | null;
  job_title: string | null;
  phone: string | null;
  timezone: string | null;
  avatar_url: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_login_at: string | null;
  /** @deprecated Accounts are provisioned on Free; never gates app access. */
  plan_selection_required: boolean;
  auth: UserAuth | null;
  onboarding: UserOnboarding | null;
}

export interface AccountBootstrapRequestOptions {
  includeAffiliateReferral?: boolean;
}

export interface UpdateProfileRequest {
  display_name?: string | null;
  company_name?: string | null;
  job_title?: string | null;
  phone?: string | null;
  timezone?: string | null;
}

export interface UpdateSettingsRequest {
  theme_mode?: string;
  notification_email?: boolean;
  notification_low_credits?: boolean;
  notification_weekly_digest?: boolean;
  marketing_opt_in?: boolean;
  default_model?: string;
  api_response_format?: string;
}

export interface WorkOSOAuthCompletionResponse {
  redirect_uri: string;
}

// Feedback types
export interface SubmitFeedbackRequest {
  rating: number;
  body: string;
  category?: string | null;
  page_context?: string | null;
}

export interface FeedbackEntry {
  feedback_id: string;
  rating: number;
  body: string;
  category: string | null;
  page_context: string | null;
  created_at: string | null;
}

export interface SubmitFeedbackResponse extends FeedbackEntry {
  message: string;
}

export interface FeedbackListResponse {
  feedback: FeedbackEntry[];
  next_cursor: string | null;
  has_more: boolean;
}

const EMPTY_MARKER_INFO: MarkerInfo = {
  marker_id: null,
  color: null,
  note: null,
  updated_at: null,
};

const normalizeMatchedFieldInstances = (raw: any): MatchedFieldInstance[] | null => {
  if (!Array.isArray(raw)) {
    return null;
  }

  const normalized = raw.reduce<MatchedFieldInstance[]>((nextItems, item) => {
    if (!item || typeof item !== 'object') {
      return nextItems;
    }

    const fieldPath = typeof item.field_path === 'string' ? item.field_path : '';
    const fieldInstancePath = typeof item.field_instance_path === 'string' ? item.field_instance_path : '';
    const fieldInstanceKey = typeof item.field_instance_key === 'string' && item.field_instance_key
      ? item.field_instance_key
      : fieldInstancePath;
    const score = typeof item.score === 'number' ? item.score : Number(item.score);
    if (!fieldPath || !fieldInstanceKey || !Number.isFinite(score)) {
      return nextItems;
    }

    nextItems.push({
      field_path: fieldPath,
      field_display_label: typeof item.field_display_label === 'string' ? item.field_display_label : undefined,
      field_instance_key: fieldInstanceKey,
      field_instance_path: fieldInstancePath,
      field_instance_display_label: typeof item.field_instance_display_label === 'string' ? item.field_instance_display_label : undefined,
      score,
      value_preview: typeof item.value_preview === 'string' ? item.value_preview : undefined,
    });
    return nextItems;
  }, []);

  return normalized.length > 0 ? normalized : null;
};

const normalizeFiniteNumber = (raw: any): number | null => {
  const normalized = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(normalized) ? normalized : null;
};

const normalizeOptionalString = (raw: any): string | null => {
  if (typeof raw !== 'string') {
    return null;
  }
  const normalized = raw.trim();
  if (!normalized) {
    return null;
  }
  const lower = normalized.toLowerCase();
  return lower === 'none' || lower === 'null' || lower === 'nil' ? null : normalized;
};

const isRecordObject = (raw: any): raw is Record<string, any> => (
  !!raw && typeof raw === 'object' && !Array.isArray(raw)
);

const normalizeStringArray = (raw: any): string[] => (
  Array.isArray(raw)
    ? raw.filter((value: unknown): value is string => typeof value === 'string')
    : []
);

const normalizeScoreMap = (raw: any): Record<string, number> | null => {
  if (!isRecordObject(raw)) {
    return null;
  }

  const normalized = Object.entries(raw).reduce<Record<string, number>>((nextValue, [key, value]) => {
    if (typeof key !== 'string' || !key) {
      return nextValue;
    }
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return nextValue;
    }
    nextValue[key] = numericValue;
    return nextValue;
  }, {});

  return Object.keys(normalized).length > 0 ? normalized : null;
};

const normalizeSearchResult = (raw: any): SearchResult => {
  const resultType: SearchResultType = raw?.result_type === 'video' ? 'video' : 'segment';
  const mediaType = raw?.media_type === 'video'
    || raw?.media_type === 'audio'
    || raw?.media_type === 'image'
    ? raw.media_type
    : null;
  const videoId = normalizeOptionalString(raw?.video_id) || '';
  const segmentId = normalizeOptionalString(raw?.segment_id);
  const runId = normalizeOptionalString(raw?.run_id) || undefined;
  const sourceRunId = normalizeOptionalString(raw?.source_run_id) || undefined;
  const promptRunId = normalizeOptionalString(raw?.prompt_run_id) || undefined;
  const sourceIndexId = normalizeOptionalString(raw?.source_index_id) || undefined;
  const identityRunId = runId || promptRunId || sourceRunId;
  const fallbackResultId = resultType === 'video'
    ? `video:${videoId}:${identityRunId || sourceIndexId || 'global'}`
    : `segment:${segmentId || videoId}:${identityRunId || sourceIndexId || 'global'}`;
  const metadata = selectSearchResultMetadata(raw?.extracted_metadata, raw?.metadata);

  return {
    result_type: resultType,
    result_id: normalizeOptionalString(raw?.result_id) || fallbackResultId,
    video_id: videoId,
    video_uri: normalizeOptionalString(raw?.video_uri) || undefined,
    segment_id: segmentId,
    start_time: normalizeFiniteNumber(raw?.start_time),
    end_time: normalizeFiniteNumber(raw?.end_time),
    preview_segment_id: normalizeOptionalString(raw?.preview_segment_id),
    preview_start_time: normalizeFiniteNumber(raw?.preview_start_time),
    preview_end_time: normalizeFiniteNumber(raw?.preview_end_time),
    preview_segment_uri: normalizeOptionalString(raw?.preview_segment_uri),
    preview_thumbnail_uri: normalizeOptionalString(raw?.preview_thumbnail_uri),
    preview_gif_uri: normalizeOptionalString(raw?.preview_gif_uri),
    segment_uri: normalizeOptionalString(raw?.segment_uri)
      || normalizeOptionalString(raw?.preview_segment_uri)
      || normalizeOptionalString(raw?.gcs_uri),
    gcs_uri: normalizeOptionalString(raw?.gcs_uri),
    playback_source_uri: normalizeOptionalString(raw?.playback_source_uri),
    playback_start_time: normalizeFiniteNumber(raw?.playback_start_time),
    playback_end_time: normalizeFiniteNumber(raw?.playback_end_time),
    thumbnail_gcs_uri: normalizeOptionalString(raw?.thumbnail_gcs_uri) || undefined,
    gif_gcs_uri: normalizeOptionalString(raw?.gif_gcs_uri) || undefined,
    thumbnail_uri: normalizeOptionalString(raw?.thumbnail_uri) || undefined,
    gif_uri: normalizeOptionalString(raw?.gif_uri) || undefined,
    video_thumbnail_gcs_uri: normalizeOptionalString(raw?.video_thumbnail_gcs_uri) || undefined,
    video_thumbnail_uri: normalizeOptionalString(raw?.video_thumbnail_uri) || undefined,
    video_thumbnail_available: Boolean(
      raw?.video_thumbnail_available ?? raw?.video_thumbnail_uri,
    ),
    video_gif_gcs_uri: normalizeOptionalString(raw?.video_gif_gcs_uri) || undefined,
    video_gif_uri: normalizeOptionalString(raw?.video_gif_uri) || undefined,
    video_gif_available: Boolean(raw?.video_gif_available ?? raw?.video_gif_uri),
    media_type: mediaType,
    thumbnail_data: undefined,
    thumbnail_available: Boolean(raw?.thumbnail_available ?? raw?.thumbnail_uri),
    gif_data: undefined,
    gif_available: Boolean(raw?.gif_available ?? raw?.gif_uri),
    text_content: normalizeOptionalString(raw?.text_content) || '',
    content_preview: normalizeOptionalString(raw?.content_preview)
      || normalizeOptionalString(raw?.text_content)
      || normalizeOptionalString(raw?.metadata_text)
      || undefined,
    metadata_text: normalizeOptionalString(raw?.metadata_text) || undefined,
    similarity_score: normalizeFiniteNumber(raw?.similarity_score),
    fused_score: normalizeFiniteNumber(raw?.fused_score) ?? undefined,
    text_score: normalizeFiniteNumber(raw?.text_score) ?? undefined,
    image_score: normalizeFiniteNumber(raw?.image_score) ?? undefined,
    text_rank: normalizeFiniteNumber(raw?.text_rank) ?? undefined,
    image_rank: normalizeFiniteNumber(raw?.image_rank) ?? undefined,
    match_type: raw?.match_type,
    reranked_score: normalizeFiniteNumber(raw?.reranked_score) ?? undefined,
    metadata,
    extracted_metadata: metadata,
    field_scores: normalizeScoreMap(raw?.field_scores),
    field_instance_scores: normalizeScoreMap(raw?.field_instance_scores),
    matched_field_paths: Array.isArray(raw?.matched_field_paths) ? raw.matched_field_paths : null,
    matched_field_instances: normalizeMatchedFieldInstances(raw?.matched_field_instances),
    matched_image_gcs_uri: normalizeOptionalString(raw?.matched_image_gcs_uri) || undefined,
    matched_image_uri: normalizeOptionalString(raw?.matched_image_uri) || undefined,
    matched_image_timestamp: normalizeFiniteNumber(raw?.matched_image_timestamp) ?? undefined,
    matched_image_score: normalizeFiniteNumber(raw?.matched_image_score) ?? undefined,
    shot_timestamp: normalizeFiniteNumber(raw?.shot_timestamp) ?? undefined,
    run_id: runId,
    source_run_id: sourceRunId,
    prompt_run_id: promptRunId,
    raw_llm_response: normalizeOptionalString(raw?.raw_llm_response) || undefined,
    video_name: normalizeOptionalString(raw?.video_name)
      || normalizeOptionalString(raw?.video_title)
      || normalizeOptionalString(raw?.title)
      || undefined,
    source_index_id: sourceIndexId,
    marker: raw?.marker ?? EMPTY_MARKER_INFO,
    extracted_metadata_markers: raw?.extracted_metadata_markers ?? raw?.metadata_markers ?? {},
  };
};

const normalizeSearchResults = <T extends SearchResult = SearchResult>(results: any[]): T[] => (
  (Array.isArray(results) ? results : []).map((result) => normalizeSearchResult(result) as T)
);

const normalizeRequestedScope = (raw: any): RequestedScope => ({
  index_ids: Array.isArray(raw?.index_ids) ? raw.index_ids.filter((value: unknown): value is string => typeof value === 'string') : [],
  prompt_run_ids: Array.isArray(raw?.prompt_run_ids) ? raw.prompt_run_ids.filter((value: unknown): value is string => typeof value === 'string') : [],
});

const normalizeAgentMessage = (raw: any): AgentMessage => ({
  id: typeof raw?.id === 'string' ? raw.id : '',
  role: raw?.role === 'assistant' || raw?.role === 'system' ? raw.role : 'user',
  content: typeof raw?.content === 'string' ? raw.content : '',
  timestamp: typeof raw?.timestamp === 'string' ? raw.timestamp : new Date().toISOString(),
  metadata: raw?.metadata && typeof raw.metadata === 'object' ? raw.metadata : {},
});

const normalizeAgentToolTraceEntry = (raw: any): AgentToolTraceEntry => ({
  tool_name: typeof raw?.tool_name === 'string' ? raw.tool_name : 'unknown_tool',
  arguments: raw?.arguments && typeof raw.arguments === 'object' ? raw.arguments : {},
  result_summary: raw?.result_summary && typeof raw.result_summary === 'object' ? raw.result_summary : {},
  started_at: typeof raw?.started_at === 'string' ? raw.started_at : null,
  completed_at: typeof raw?.completed_at === 'string' ? raw.completed_at : null,
  error: typeof raw?.error === 'string' ? raw.error : null,
});

const normalizeAgentResultSet = (raw: any): AgentResultSet => ({
  result_set_id: typeof raw?.result_set_id === 'string' ? raw.result_set_id : '',
  position: Number.isInteger(raw?.position) && raw.position >= 0 ? raw.position : null,
  label: typeof raw?.label === 'string' ? raw.label : 'Results',
  result_level: raw?.result_level === 'video' || raw?.result_level === 'mixed' ? raw.result_level : 'segment',
  origin: raw?.origin === 'memory' || raw?.origin === 'selection' ? raw.origin : 'search',
  source_query: typeof raw?.source_query === 'string' ? raw.source_query : null,
  summary: typeof raw?.summary === 'string' ? raw.summary : null,
  result_count: typeof raw?.result_count === 'number' ? raw.result_count : Array.isArray(raw?.results) ? raw.results.length : 0,
  results: normalizeSearchResults(raw?.results),
});

const normalizeAgentTurn = (raw: any): AgentTurn => ({
  turn_id: typeof raw?.turn_id === 'string' ? raw.turn_id : '',
  session_id: typeof raw?.session_id === 'string' ? raw.session_id : '',
  status: raw?.status === 'processing' || raw?.status === 'failed' ? raw.status : 'completed',
  created_at: typeof raw?.created_at === 'string' ? raw.created_at : new Date().toISOString(),
  updated_at: typeof raw?.updated_at === 'string' ? raw.updated_at : new Date().toISOString(),
  completed_at: typeof raw?.completed_at === 'string' ? raw.completed_at : null,
  user_message: normalizeAgentMessage(raw?.user_message),
  assistant_message: raw?.assistant_message ? normalizeAgentMessage(raw.assistant_message) : null,
  effective_scope: normalizeRequestedScope(raw?.effective_scope),
  tool_trace: Array.isArray(raw?.tool_trace) ? raw.tool_trace.map(normalizeAgentToolTraceEntry) : [],
  result_sets: Array.isArray(raw?.result_sets) ? raw.result_sets.map(normalizeAgentResultSet) : [],
  stats: {
    tool_calls: typeof raw?.stats?.tool_calls === 'number' ? raw.stats.tool_calls : 0,
    review_loops: typeof raw?.stats?.review_loops === 'number' ? raw.stats.review_loops : 0,
    evidence_result_count: typeof raw?.stats?.evidence_result_count === 'number' ? raw.stats.evidence_result_count : 0,
    selected_result_count: typeof raw?.stats?.selected_result_count === 'number' ? raw.stats.selected_result_count : 0,
    duration_ms: typeof raw?.stats?.duration_ms === 'number' ? raw.stats.duration_ms : null,
    model_name: typeof raw?.stats?.model_name === 'string' ? raw.stats.model_name : null,
  },
  error: raw?.error && typeof raw.error === 'object'
    ? {
        error_code: typeof raw.error.error_code === 'string' ? raw.error.error_code : 'unknown_error',
        message: typeof raw.error.message === 'string' ? raw.error.message : 'Unknown error',
        details: raw.error.details && typeof raw.error.details === 'object' ? raw.error.details : {},
      }
    : null,
});

const normalizeAgentSessionSummary = (raw: any): AgentSessionSummary => ({
  session_id: typeof raw?.session_id === 'string' ? raw.session_id : '',
  user_id: typeof raw?.user_id === 'string' ? raw.user_id : '',
  created_at: typeof raw?.created_at === 'string' ? raw.created_at : new Date().toISOString(),
  updated_at: typeof raw?.updated_at === 'string' ? raw.updated_at : new Date().toISOString(),
  title: typeof raw?.title === 'string' ? raw.title : null,
  is_active: raw?.is_active !== false,
  turn_count: typeof raw?.turn_count === 'number' ? raw.turn_count : 0,
  last_turn_id: typeof raw?.last_turn_id === 'string' ? raw.last_turn_id : null,
  last_turn_status: raw?.last_turn_status === 'processing' || raw?.last_turn_status === 'failed' || raw?.last_turn_status === 'completed'
    ? raw.last_turn_status
    : null,
});

const normalizeAgentSessionDetail = (raw: any): AgentSessionDetail => ({
  ...normalizeAgentSessionSummary(raw),
  turns: Array.isArray(raw?.turns) ? raw.turns.map(normalizeAgentTurn) : [],
});

const normalizeChatStreamEvent = (raw: any): ChatStreamEvent => {
  const eventId = typeof raw?.event_id === 'string' ? raw.event_id : undefined;
  const eventSeq = typeof raw?.event_seq === 'number' ? raw.event_seq : undefined;
  switch (raw?.type) {
    case 'turn_started':
      return {
        type: 'turn_started',
        event_id: eventId,
        event_seq: eventSeq,
        turn_id: typeof raw?.turn_id === 'string' ? raw.turn_id : '',
        session_id: typeof raw?.session_id === 'string' ? raw.session_id : '',
        timestamp: typeof raw?.timestamp === 'string' ? raw.timestamp : new Date().toISOString(),
      };
    case 'turn_resumed':
      return {
        type: 'turn_resumed',
        event_id: eventId,
        event_seq: eventSeq,
        turn_id: typeof raw?.turn_id === 'string' ? raw.turn_id : '',
        session_id: typeof raw?.session_id === 'string' ? raw.session_id : '',
        status: raw?.status === 'completed' || raw?.status === 'failed' ? raw.status : 'processing',
        timestamp: typeof raw?.timestamp === 'string' ? raw.timestamp : new Date().toISOString(),
      };
    case 'agent_state':
      return {
        type: 'agent_state',
        event_id: eventId,
        event_seq: eventSeq,
        turn_id: typeof raw?.turn_id === 'string' ? raw.turn_id : '',
        state: raw?.state === 'prepare_turn' || raw?.state === 'tool_loop' || raw?.state === 'review_evidence' || raw?.state === 'compose_response'
          ? raw.state
          : 'understand_request',
        message: typeof raw?.message === 'string' ? raw.message : '',
        progress: typeof raw?.progress === 'number' ? raw.progress : 0,
        loop_index: typeof raw?.loop_index === 'number' ? raw.loop_index : 0,
      };
    case 'tool_call_started':
      return {
        type: 'tool_call_started',
        event_id: eventId,
        event_seq: eventSeq,
        turn_id: typeof raw?.turn_id === 'string' ? raw.turn_id : '',
        tool_name: typeof raw?.tool_name === 'string' ? raw.tool_name : 'unknown_tool',
        arguments: raw?.arguments && typeof raw.arguments === 'object' ? raw.arguments : {},
      };
    case 'tool_call_result':
      return {
        type: 'tool_call_result',
        event_id: eventId,
        event_seq: eventSeq,
        turn_id: typeof raw?.turn_id === 'string' ? raw.turn_id : '',
        tool_name: typeof raw?.tool_name === 'string' ? raw.tool_name : 'unknown_tool',
        result_summary: raw?.result_summary && typeof raw.result_summary === 'object' ? raw.result_summary : {},
      };
    case 'turn_completed':
      return {
        type: 'turn_completed',
        event_id: eventId,
        event_seq: eventSeq,
        turn: normalizeAgentTurn(raw?.turn),
      };
    default:
      return {
        type: 'error',
        event_id: eventId,
        event_seq: eventSeq,
        error_code: typeof raw?.error_code === 'string' ? raw.error_code : 'unknown_error',
        message: typeof raw?.message === 'string' ? raw.message : 'Unknown chat stream error',
        details: raw?.details && typeof raw.details === 'object' ? raw.details : {},
      };
  }
};

// API methods
const openRealtimeStream = (
  path: string, method: 'GET' | 'POST', body: unknown,
  onEvent: (event: RealtimeStreamEvent) => void,
  onError?: (error: unknown) => void,
): (() => void) => {
  const controller = new AbortController();
  let terminal = false;
  void (async () => {
    try {
      const token = requireNonEmptyFirebaseBearerToken(await requireFirebaseTokenProvider()());
      if (controller.signal.aborted) return;
      const response = await fetch(`${REALTIME_API_BASE_URL}${path}`, {
        method, headers: { Accept: 'text/event-stream', Authorization: `Bearer ${token}`, ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}) },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }), signal: controller.signal,
      });
      if (!response.ok) {
        const { message } = await readFetchErrorPayload(response);
        throw new Error(message || `Realtime session failed (${response.status})`);
      }
      const reader = response.body?.getReader();
      if (!reader) throw new Error('Realtime session has no response body');
      onEvent({ type: 'realtime.session.connected', payload: {} });
      const decoder = new TextDecoder();
      let buffer = ''; let eventType = ''; let data: string[] = [];
      while (!controller.signal.aborted && !terminal) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const lines = buffer.split('\n'); buffer = lines.pop() || '';
        for (const raw of lines) {
          const line = raw.replace(/\r$/, '');
          if (!line) {
            if (eventType && data.length) {
              const event = { type: eventType, payload: JSON.parse(data.join('\n')) };
              terminal = eventType === 'realtime.session.idle';
              onEvent(event);
            }
            eventType = ''; data = [];
          } else if (line.startsWith('event:')) eventType = line.slice(6).trim();
          else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
          if (terminal) break;
        }
      }
      if (terminal) { await reader.cancel(); return; }
      if (!controller.signal.aborted) throw new Error('Realtime session disconnected. Start a feed to reconnect.');
    } catch (error) {
      if (!controller.signal.aborted) onError?.(error);
    }
  })();
  return () => controller.abort();
};

export const apiService = {
  async createPreviewGrants(
    items: PreviewGrantRequestItem[],
    signal?: AbortSignal,
  ): Promise<PreviewGrantResponse> {
    if (items.length === 0 || items.length > 24) {
      throw new Error('Preview grant batches require between 1 and 24 identities.');
    }
    const response = await api.post<PreviewGrantResponse>(
      '/media/preview-grants',
      { items } satisfies PreviewGrantRequest,
      { signal },
    );
    return response.data;
  },

  async listRealtimeProjects(): Promise<RealtimeProject[]> {
    return (await api.get(realtimeRequestUrl('/realtime/projects'))).data;
  },
  async createRealtimeProject(name: string): Promise<RealtimeProject> {
    return (await api.post(realtimeRequestUrl('/realtime/projects'), { name })).data;
  },
  async updateRealtimeProject(projectId: string, name: string): Promise<RealtimeProject> {
    return (await api.patch(realtimeRequestUrl(`/realtime/projects/${projectId}`), { name })).data;
  },
  async listRealtimeRecordings(projectId: string, signal?: AbortSignal): Promise<RealtimeRecording[]> {
    return (await api.get(realtimeRequestUrl(`/realtime/projects/${projectId}/recordings`), { signal })).data;
  },
  async uploadRealtimeRecording(projectId: string, file: File, onProgress?: (percent: number) => void): Promise<RealtimeRecording> {
    const boundary = captureProtectedTransportBoundary();
    const contentType = file.type || 'application/octet-stream';
    const { data: grant } = await api.post(realtimeRequestUrl(`/realtime/projects/${projectId}/recordings/upload`), {
      name: file.name, content_type: contentType, size_bytes: file.size,
    }, { signal: boundary.signal });
    assertProtectedTransportBoundary(boundary);
    // The storage grant authenticates this PUT; never forward the API bearer token.
    await axios.put(grant.upload_url, file, {
      headers: { 'Content-Type': grant.content_type || contentType }, signal: boundary.signal, timeout: UPLOAD_REQUEST_TIMEOUT_MS,
      onUploadProgress: progress => onProgress?.(Math.min(100, Math.round(progress.loaded / file.size * 100))),
    });
    assertProtectedTransportBoundary(boundary);
    const response = await api.post(realtimeRequestUrl(`/realtime/projects/${projectId}/recordings/${grant.recording_id}/complete`), {}, {
      signal: boundary.signal, timeout: SEARCH_REQUEST_TIMEOUT_MS,
    });
    assertProtectedTransportBoundary(boundary);
    return response.data;
  },
  async getRealtimeRecordingPlayback(projectId: string, recordingId: string, signal?: AbortSignal): Promise<{ playback_url: string }> {
    return (await api.get(realtimeRequestUrl(`/realtime/projects/${projectId}/recordings/${recordingId}/playback`), { signal })).data;
  },
  async createRealtimeFeed(projectId: string, data: CreateRealtimeFeedRequest): Promise<RealtimeFeed> {
    return (await api.post(realtimeRequestUrl(`/realtime/projects/${projectId}/feeds`), data)).data;
  },
  async listRealtimeFeeds(projectId: string, signal?: AbortSignal): Promise<RealtimeFeed[]> {
    return (await api.get(realtimeRequestUrl(`/realtime/projects/${projectId}/feeds`), { signal, timeout: 15000 })).data;
  },
  async getRealtimeFeed(projectId: string, feedId: string): Promise<RealtimeFeed> {
    return (await api.get(realtimeRequestUrl(`/realtime/projects/${projectId}/feeds/${feedId}`))).data;
  },
  async updateRealtimeFeed(projectId: string, feedId: string, data: UpdateRealtimeFeedRequest): Promise<RealtimeFeed> {
    return (await api.patch(realtimeRequestUrl(`/realtime/projects/${projectId}/feeds/${feedId}`), data)).data;
  },
  async startRealtimeFeed(projectId: string, feedId: string, data: StartRealtimeFeedRequest = {}): Promise<RealtimeFeed> {
    return (await api.post(realtimeRequestUrl(`/realtime/projects/${projectId}/feeds/${feedId}/start`), data)).data;
  },
  startRealtimeFeedStream(projectId: string, feedId: string, data: StartRealtimeFeedRequest, onEvent: (event: RealtimeStreamEvent) => void, onError?: (error: unknown) => void): () => void {
    return openRealtimeStream(`/realtime/projects/${projectId}/feeds/${feedId}/start-stream`, 'POST', data, onEvent, onError);
  },
  async stopRealtimeFeed(projectId: string, feedId: string): Promise<RealtimeFeed> {
    return (await api.post(realtimeRequestUrl(`/realtime/projects/${projectId}/feeds/${feedId}/stop`), {})).data;
  },
  async listRealtimeEvents(projectId: string, scope: RealtimeEventScope = {}, cursor?: string | null, signal?: AbortSignal): Promise<RealtimeEventPage> {
    return (await api.get(realtimeRequestUrl(`/realtime/projects/${projectId}/events`), {
      params: { ...scope, feed_ids: scope.feed_ids?.join(','), alert_levels: scope.alert_levels?.join(','), limit: 50, cursor: cursor || undefined }, signal, timeout: 15000,
    })).data;
  },
  async getRealtimeFields(projectId: string, feedIds?: string[], signal?: AbortSignal): Promise<RealtimeFieldDefinition[]> {
    return (await api.get(realtimeRequestUrl(`/realtime/projects/${projectId}/fields`), { params: { feed_ids: feedIds?.join(',') }, signal })).data.fields;
  },
  async searchRealtimeEvents(projectId: string, request: RealtimeSearchRequest, signal?: AbortSignal): Promise<RealtimeEventPage> {
    return (await api.post(realtimeRequestUrl(`/realtime/projects/${projectId}/search`), { ...request, ...(request.conditions ? { conditions: serializeFilterConditions(request.conditions) } : {}) }, { signal })).data;
  },
  async getRealtimeAlertStats(projectId: string, request: RealtimeAlertStatsRequest = {}, signal?: AbortSignal): Promise<RealtimeAlertStatsPage> {
    return (await api.post(realtimeRequestUrl(`/realtime/projects/${projectId}/alert-stats`), request, { signal, timeout: 15000 })).data;
  },
  async retryRealtimeEventOperation(projectId: string, eventId: string, operation: 'indexing' | 'thumbnail' | 'storage'): Promise<RealtimeEvent> {
    return (await api.post(realtimeRequestUrl(`/realtime/projects/${projectId}/events/${eventId}/retry`), { operation })).data;
  },

  async getRealtimePlaybackConfig(relativeUrl: string): Promise<{
    url: string;
    authorization: string;
  }> {
    const token = requireNonEmptyFirebaseBearerToken(
      await requireFirebaseTokenProvider()(),
    );
    const normalizedPath = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
    return {
      url: `${REALTIME_API_BASE_URL}${normalizedPath}`,
      authorization: `Bearer ${token}`,
    };
  },

  async getRealtimeClip(relativeUrl: string): Promise<Blob> {
    const response = await api.get(realtimeRequestUrl(relativeUrl), { responseType: 'blob' });
    return response.data;
  },

  subscribeRealtimeFeeds(onEvent: (event: RealtimeStreamEvent) => void, onError?: (error: unknown) => void): () => void {
    return openRealtimeStream('/realtime/feeds/stream', 'GET', undefined, onEvent, onError);
  },

  // Index management
  async getIndexes(options?: { includePreview?: boolean }): Promise<Index[]> {
    const response = await api.get('/indexes', options?.includePreview
      ? { params: { include_preview: true } }
      : undefined);
    return response.data;
  },

  async createIndex(data: CreateIndexRequest, idempotencyKey?: string): Promise<Index> {
    const response = await api.post('/indexes', data, {
      headers: buildIdempotencyHeaders(
        resolveIdempotencyKey(idempotencyKey, 'index-create'),
      ),
    });
    return response.data;
  },

  async getIndex(indexId: string): Promise<Index> {
    const response = await api.get(`/indexes/${indexId}`);
    return response.data;
  },

  async deleteIndex(indexId: string): Promise<IndexDeletionResponse> {
    const response = await api.delete(`/indexes/${indexId}`);
    return response.data;
  },

  async getIndexDeletion(indexId: string): Promise<IndexDeletionResponse> {
    const response = await api.get(`/indexes/${indexId}/deletion`);
    return response.data;
  },

  async updateIndexPrompt(indexId: string, promptId: string | null): Promise<{
    index_id: string;
    index_name: string;
    prompt_id: string | null;
    prompt_name: string | null;
    message: string;
  }> {
    const response = await api.put(`/indexes/${indexId}/update-prompt`, {
      prompt_id: promptId
    });
    return response.data;
  },

  // Video management
  async getVideosInIndex(
    indexId: string,
    limit: number = 50,
    cursor?: string,
    options?: { view?: 'gallery' },
  ): Promise<PaginatedResponse<Video>> {
    const params: Record<string, any> = { limit };
    if (cursor) params.cursor = cursor;
    if (options?.view) params.view = options.view;
    const response = await api.get(`/indexes/${indexId}/videos`, readConfig(params));
    return response.data;
  },

  async createVideo(data: CreateVideoRequest): Promise<ImportJob> {
    notifyProcessingUploadIntent();
    const response = await api.post('/videos', data);
    return response.data;
  },

  async getVideo(videoId: string): Promise<Video> {
    const response = await api.get(`/videos/${videoId}`);
    return response.data;
  },

  async deleteVideo(videoId: string): Promise<VideoDeletionResponse> {
    const response = await api.delete(`/videos/${videoId}`);
    return response.data;
  },

  async getVideoDeletion(videoId: string): Promise<VideoDeletionResponse> {
    const response = await api.get(`/videos/${videoId}/deletion`);
    return response.data;
  },

  async processVideo(videoId: string, segmentDuration: number = 10, promptId?: string): Promise<void> {
    const params: any = { segment_duration: segmentDuration };
    if (promptId) {
      params.prompt_id = promptId;
    }
    await api.post(`/videos/${videoId}/process`, null, { params });
  },

  // Search
  async searchVideos(indexId: string, searchData: SearchRequest, signal?: AbortSignal): Promise<SearchResult[]> {
    const response = await api.post(`/indexes/${indexId}/search`, searchData, {
      signal,
      timeout: SEARCH_REQUEST_TIMEOUT_MS,
    });
    return normalizeSearchResults(response.data);
  },

  // Image-based search
  async searchByImage(indexId: string, imageData: string, topK: number = 20, runIds?: string[] | null, indexIds?: string[] | null, signal?: AbortSignal, view?: 'full' | 'gallery'): Promise<SearchResult[]> {
    const requestBody = {
      image_data: imageData,
      top_k: topK,
      run_ids: runIds,
      index_ids: indexIds,
      ...(view ? { view } : {}),
    };
    const response = await api.post(`/indexes/${indexId}/image-search`, requestBody, {
      signal,
      timeout: SEARCH_REQUEST_TIMEOUT_MS,
    });
    return normalizeSearchResults(response.data);
  },

  // Multimodal search (text + image with RRF fusion)
  async searchMultimodal(
    indexId: string,
    options: {
      textQuery?: string;
      imageData?: string;
      topK?: number;
      textWeight?: number;
      imageWeight?: number;
      runIds?: string[] | null;
      searchFields?: string[] | null;
      indexIds?: string[] | null;
      view?: 'full' | 'gallery';
    },
    signal?: AbortSignal,
  ): Promise<MultimodalSearchResult[]> {
    const { textQuery, imageData, topK = 20, textWeight = 0.5, imageWeight = 0.5, runIds, searchFields, indexIds, view } = options;
    const requestBody = {
      text_query: textQuery,
      image_data: imageData,
      top_k: topK,
      text_weight: textWeight,
      image_weight: imageWeight,
      run_ids: runIds,
      search_fields: searchFields,
      index_ids: indexIds,
      ...(view ? { view } : {}),
    };
    const response = await api.post(`/indexes/${indexId}/multimodal-search`, requestBody, {
      signal,
      timeout: SEARCH_REQUEST_TIMEOUT_MS,
    });
    return normalizeSearchResults<MultimodalSearchResult>(response.data);
  },

  // Get signed URL for video segment
  async getSignedUrl(
    gcsUri: string,
    signal?: AbortSignal,
    forceRefresh = false,
    context: SignedUrlContext = {},
  ): Promise<{
    signed_url: string; 
    expires_at: string; 
  }> {
    const response = await api.post(
      '/videos/signed-url',
      {
        gcs_uri: gcsUri,
        force_refresh: forceRefresh,
        ...(context.videoId ? { video_id: context.videoId } : {}),
        ...(context.segmentId ? { segment_id: context.segmentId } : {}),
      },
      { signal },
    );
    return response.data;
  },


  // Upload video file
  async uploadVideo(
    file: File,
    title: string,
    indexId: string | null,
    providedIdempotencyKey?: string,
    expectedUserId?: string,
  ): Promise<ImportJob> {
    if (exceedsMediaUploadSizeLimit(file.size)) {
      throw new Error(MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE);
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    const normalizedIndexId = normalizeUploadIndexId(indexId);
    if (normalizedIndexId) {
      formData.append('index_id', normalizedIndexId);
    }
    const requestScope = uploadRequestScope(
      file,
      title,
      normalizedIndexId,
    );
    const idempotencyKey = retainUploadIdempotencyKey(
      file,
      requestScope,
      providedIdempotencyKey,
    );
    const normalizedExpectedUserId = String(expectedUserId || '').trim();
    const transportBoundary = normalizedExpectedUserId
      ? captureProtectedTransportBoundary()
      : null;
    if (transportBoundary && transportBoundary.userId !== normalizedExpectedUserId) {
      throw createAuthBoundaryUnsettledError();
    }

    try {
      notifyProcessingUploadIntent(normalizedExpectedUserId || undefined);
      const response = await api.post('/videos/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...buildIdempotencyHeaders(idempotencyKey),
        },
        timeout: UPLOAD_REQUEST_TIMEOUT_MS,
        ...(transportBoundary ? { signal: transportBoundary.signal } : {}),
      });
      if (transportBoundary) assertProtectedTransportBoundary(transportBoundary);
      if (!providedIdempotencyKey) {
        uploadIdempotencyKeys.get(file)?.delete(requestScope);
      }
      return response.data;
    } catch (error) {
      if (transportBoundary) {
        return rethrowProtectedTransportRequestError(transportBoundary, error);
      }
      throw error;
    }
  },

  async uploadGoogleDriveFile({
    fileId,
    resourceKey,
    title,
    indexId,
    googleAccessToken,
    idempotencyKey,
    expectedUserId,
  }: GoogleDriveUploadRequest): Promise<ImportJob> {
    const normalizedFileId = fileId.trim();
    const normalizedResourceKey = String(resourceKey || '').trim();
    const normalizedTitle = String(title || '').trim();
    const normalizedIndexId = normalizeUploadIndexId(indexId);
    const normalizedGoogleAccessToken = googleAccessToken.trim();
    const normalizedIdempotencyKey = idempotencyKey.trim();
    const normalizedExpectedUserId = expectedUserId.trim();
    if (
      !normalizedFileId
      || !normalizedGoogleAccessToken
      || !normalizedIdempotencyKey
      || !normalizedExpectedUserId
    ) {
      throw new Error('Google Drive upload credentials are incomplete');
    }
    const transportBoundary = captureProtectedTransportBoundary();
    if (transportBoundary.userId !== normalizedExpectedUserId) {
      throw createAuthBoundaryUnsettledError();
    }

    try {
      notifyProcessingUploadIntent(normalizedExpectedUserId);
      const response = await api.post('/videos/upload/google-drive', {
        file_id: normalizedFileId,
        ...(normalizedResourceKey ? { resource_key: normalizedResourceKey } : {}),
        ...(normalizedTitle ? { title: normalizedTitle } : {}),
        ...(normalizedIndexId ? { index_id: normalizedIndexId } : {}),
      }, {
        headers: {
          [GOOGLE_DRIVE_ACCESS_TOKEN_HEADER]: normalizedGoogleAccessToken,
          ...buildIdempotencyHeaders(normalizedIdempotencyKey),
        },
        timeout: UPLOAD_REQUEST_TIMEOUT_MS,
        signal: transportBoundary.signal,
      });
      assertProtectedTransportBoundary(transportBoundary);
      return response.data;
    } catch (error) {
      scrubGoogleDriveTokenFromTransport(error);
      return rethrowProtectedTransportRequestError(transportBoundary, error);
    }
  },

  // Get video segments
  async getVideoSegments(
    videoId: string,
    runId?: string,
    latestRun?: boolean,
    options: {
      view?: 'references';
      segmentId?: string;
      signal?: AbortSignal;
    } = {},
  ): Promise<SegmentResponse[]> {
    const params: any = {};
    if (runId) params.run_id = runId;
    if (latestRun) params.latest_run = latestRun;
    if (options.view) params.view = options.view;
    if (options.segmentId) params.segment_id = options.segmentId;
    const response = await api.get<PaginatedResponse<SegmentResponse>>(
      `/videos/${videoId}/segments`,
      { params, signal: options.signal },
    );
    return response.data.data;
  },

  async getVideoSegment(
    videoId: string,
    segmentId: string,
    runId?: string | null,
    signal?: AbortSignal,
  ): Promise<SegmentResponse | null> {
    const segments = await this.getVideoSegments(
      videoId,
      runId || undefined,
      false,
      { view: 'references', segmentId, signal },
    );
    return segments.find((segment) => segment.segment_id === segmentId) || null;
  },

  async getAllVideoSegments(videoId: string, runId?: string | null): Promise<SegmentResponse[]> {
    const allSegments: SegmentResponse[] = [];
    const seenCursors = new Set<string>();
    let cursor: string | undefined;

    for (let pageCount = 0; pageCount < 1000; pageCount += 1) {
      const params: Record<string, any> = { limit: 100, view: 'references' };
      const normalizedRunId = normalizeOptionalString(runId);
      if (normalizedRunId) params.run_id = normalizedRunId;
      if (cursor) params.cursor = cursor;

      const response = await api.get<PaginatedResponse<SegmentResponse>>(`/videos/${videoId}/segments`, { params });
      const page = response.data;
      allSegments.push(...(Array.isArray(page?.data) ? page.data : []));

      const hasMore = Boolean(page?.pagination?.has_more);
      const nextCursor = normalizeOptionalString(page?.pagination?.next_cursor);
      if (!hasMore) {
        return allSegments;
      }

      if (!nextCursor) {
        throw new Error(`Missing pagination cursor while loading video segments for ${videoId}`);
      }

      if (seenCursors.has(nextCursor)) {
        throw new Error(`Repeated pagination cursor while loading video segments for ${videoId}`);
      }

      seenCursors.add(nextCursor);
      cursor = nextCursor;
    }

    throw new Error(`Exceeded maximum pagination depth while loading video segments for ${videoId}`);
  },

  // Prompt management
  async createPrompt(data: CreatePromptRequest, idempotencyKey?: string): Promise<Prompt> {
    const operationKey = resolveIdempotencyKey(idempotencyKey, 'prompt-create');
    const response = await api.post(
      data.is_realtime ? realtimeRequestUrl('/prompts') : '/prompts',
      data,
      { headers: buildIdempotencyHeaders(operationKey), ...(data.is_realtime ? { params: { is_realtime: true } } : {}) },
    );
    return response.data;
  },

  async generatePromptDraft(
    instruction: string,
    options: { idempotencyKey?: string; executionConfig?: PromptExecutionConfig } = {},
    signal?: AbortSignal,
  ): Promise<GeneratedPromptDraft> {
    const idempotencyKey = resolveIdempotencyKey(options.idempotencyKey, 'prompt-assistant');
    const response = await api.post(
      '/prompts/generate',
      { instruction, ...(options.executionConfig ? { execution_config: options.executionConfig } : {}) },
      {
        signal,
        timeout: 60000,
        headers: buildIdempotencyHeaders(idempotencyKey),
      },
    );
    const rawDraft = isRecordObject(response.data) ? response.data : {};
    const rawVideoLevel = isRecordObject(rawDraft.video_level) ? rawDraft.video_level : null;
    const rawSemanticIndexing = isRecordObject(rawDraft.semantic_indexing)
      ? rawDraft.semantic_indexing
      : null;
    return {
      name: typeof rawDraft.name === 'string' ? rawDraft.name : '',
      description: typeof rawDraft.description === 'string' ? rawDraft.description : '',
      prompt_text: typeof rawDraft.prompt_text === 'string' ? rawDraft.prompt_text : '',
      json_schema: isRecordObject(rawDraft.json_schema)
        ? rawDraft.json_schema
        : { type: 'object', properties: {} },
      video_level: rawVideoLevel
        ? {
            instructions_text: typeof rawVideoLevel.instructions_text === 'string'
              ? rawVideoLevel.instructions_text
              : '',
            json_schema: isRecordObject(rawVideoLevel.json_schema)
              ? rawVideoLevel.json_schema
              : { type: 'object', properties: {} },
          }
        : null,
      semantic_indexing: rawSemanticIndexing
        ? {
            disabled_segment_fields: normalizeStringArray(rawSemanticIndexing.disabled_segment_fields),
            disabled_video_level_fields: normalizeStringArray(rawSemanticIndexing.disabled_video_level_fields),
          }
        : {
            disabled_segment_fields: [],
            disabled_video_level_fields: [],
          },
      execution_config: isRecordObject(rawDraft.execution_config)
        ? rawDraft.execution_config as unknown as PromptExecutionConfig
        : null,
      selection_filter: isRecordObject(rawDraft.selection_filter)
        ? rawDraft.selection_filter as unknown as PromptSelectionFilter
        : null,
    };
  },

  async defineWorkflowPrompt(
    instruction: string,
    options: { idempotencyKey?: string; expectedUserId?: string } = {},
  ): Promise<WorkflowDefineResponse> {
    const transportBoundary = captureProtectedTransportBoundary();
    const expectedUserId = String(options.expectedUserId || '').trim();
    if (expectedUserId && transportBoundary.userId !== expectedUserId) {
      throw createAuthBoundaryUnsettledError();
    }
    const idempotencyKey = resolveIdempotencyKey(
      options.idempotencyKey,
      'workflow-prompt-define',
    );
    try {
      const response = await api.post<WorkflowDefineResponse>(
        '/workflow/define',
        { instruction, save: true },
        {
          timeout: 60000,
          headers: buildIdempotencyHeaders(idempotencyKey),
          signal: transportBoundary.signal,
        },
      );
      assertProtectedTransportBoundary(transportBoundary);
      return response.data;
    } catch (error) {
      return rethrowProtectedTransportRequestError(transportBoundary, error);
    }
  },

  async updatePrompt(promptId: string, data: Partial<CreatePromptRequest>): Promise<Prompt> {
    const response = await api.put(data.is_realtime ? realtimeRequestUrl(`/prompts/${promptId}`) : `/prompts/${promptId}`, data, data.is_realtime ? { params: { is_realtime: true } } : undefined);
    return response.data;
  },

  async getPrompts(activeOnly: boolean = true, includeDefaults: boolean = true, kind: EngineKind = 'video'): Promise<PromptListResponse> {
    const response = await api.get(kind === 'realtime' ? realtimeRequestUrl('/prompts') : '/prompts', {
      params: {
        active_only: activeOnly,
        include_defaults: includeDefaults,
        is_realtime: kind === 'realtime',
      }
    });
    return response.data;
  },

  async getPrompt(promptId: string): Promise<Prompt> {
    const response = await api.get(`/prompts/${promptId}`);
    return response.data;
  },

  async deletePrompt(promptId: string, force: boolean = false, isRealtime: boolean = false): Promise<void> {
    await api.delete(isRealtime ? realtimeRequestUrl(`/prompts/${promptId}`) : `/prompts/${promptId}`, {
      params: { force, is_realtime: isRealtime }
    });
  },

  // Prompt Run management
  async estimatePromptRunCost(data: PromptRunEstimateRequest): Promise<CostEstimate> {
    const response = await api.post('/prompt-runs/estimate', data);
    return response.data;
  },

  async executePromptRun(data: ExecutePromptRequest, idempotencyKey?: string): Promise<PromptRun> {
    const headers = buildIdempotencyHeaders(resolveIdempotencyKey(idempotencyKey, 'prompt-run-execute'));
    const response = await api.post('/prompt-runs/execute', data, { headers });
    return response.data;
  },

  async getPromptRun(runId: string): Promise<PromptRun> {
    const response = await api.get(`/prompt-runs/${runId}`);
    return response.data;
  },

  async getPromptRunWorkflowResults(
    runId: string,
    level: 'video' | 'segment',
    limit: number = 50,
    cursor?: string,
    options: { videoId?: string; signal?: AbortSignal } = {},
  ): Promise<PaginatedResponse<PromptRunWorkflowResult>> {
    const response = await api.get(`/prompt-runs/${runId}/workflow-results`, {
      params: {
        level,
        limit,
        ...(cursor ? { cursor } : {}),
        ...(options.videoId ? { video_id: options.videoId } : {}),
      },
      signal: options.signal,
    });
    return response.data;
  },

  async getPromptRunVideoResult(
    runId: string,
    videoId: string,
    signal?: AbortSignal,
  ): Promise<VideoPromptRunResult> {
    const path = `/prompt-runs/${runId}/videos/${videoId}/video-result`;
    const response = signal
      ? await api.get(path, { signal })
      : await api.get(path);
    return response.data;
  },

  async getPromptRuns(limit: number = 200): Promise<PromptRun[]> {
    const response = await api.get('/prompt-runs', { params: { limit } });
    return response.data;
  },

  async getPromptRunsByIndex(indexId: string, status?: string): Promise<PromptRun[]> {
    const response = await api.get(`/indexes/${indexId}/prompt-runs`, readConfig(status ? { status } : {}));
    return response.data.data;
  },

  async getPromptRunsByIndexPage(indexId: string, limit: number = 50, cursor?: string, status?: string): Promise<PaginatedResponse<PromptRun>> {
    const params: Record<string, any> = { limit };
    if (cursor) {
      params.cursor = cursor;
    }
    if (status) {
      params.status = status;
    }
    const response = await api.get(`/indexes/${indexId}/prompt-runs`, readConfig(params));
    return response.data;
  },

  async getAllPromptRunsByIndex(indexId: string, limit: number = 50, maxPages: number = 40, status?: string): Promise<PromptRun[]> {
    const allRuns: PromptRun[] = [];
    let cursor: string | undefined;
    let pageCount = 0;

    while (pageCount < maxPages) {
      const page = await this.getPromptRunsByIndexPage(indexId, limit, cursor, status);
      allRuns.push(...(page.data || []));
      pageCount += 1;

      if (!page.pagination?.has_more || !page.pagination?.next_cursor) {
        break;
      }

      cursor = page.pagination.next_cursor;
    }

    const dedupedByRunId = new Map<string, PromptRun>();
    allRuns.forEach((run) => {
      dedupedByRunId.set(run.run_id, run);
    });

    return Array.from(dedupedByRunId.values());
  },

  async getPromptRunsByVideo(videoId: string): Promise<PromptRun[]> {
    const response = await api.get(`/videos/${videoId}/prompt-runs`);
    return response.data;
  },

  async getPromptRunFailedSegments(runId: string): Promise<PromptRunFailedSegmentsManifest> {
    const response = await api.get(`/prompt-runs/${runId}/failed-segments`);
    return response.data;
  },

  // Metadata Export management
  async exportIndexMetadata(
    indexId: string,
    promptRunIds?: string[],
    destination?: ExportDestinationOptions,
    idempotencyKey?: string,
  ): Promise<{ export_id: string; status: string }> {
    const params = promptRunIds && promptRunIds.length > 0
      ? { "prompt_run_ids[]": promptRunIds }
      : {};
    const payload: Record<string, any> = {};
    if (promptRunIds && promptRunIds.length > 0) {
      payload.prompt_run_ids = promptRunIds;
    }
    if (destination?.destination_connector_id) {
      payload.destination_connector_id = destination.destination_connector_id;
    }
    if (destination?.destination_subpath) {
      payload.destination_subpath = destination.destination_subpath;
    }
    if (destination?.result_scope) {
      payload.result_scope = destination.result_scope;
    }
    const response = await api.post(
      `/exports/index/${indexId}`,
      Object.keys(payload).length > 0 ? payload : null,
      {
        params,
        headers: buildIdempotencyHeaders(
          resolveIdempotencyKey(idempotencyKey, `metadata-export-index:${indexId}`),
        ),
      },
    );
    return response.data;
  },

  async exportPromptRunMetadata(
    runId: string,
    destination?: ExportDestinationOptions,
    idempotencyKey?: string,
  ): Promise<{ export_id: string; status: string }> {
    const payload: Record<string, any> = {};
    if (destination?.destination_connector_id) {
      payload.destination_connector_id = destination.destination_connector_id;
    }
    if (destination?.destination_subpath) {
      payload.destination_subpath = destination.destination_subpath;
    }
    if (destination?.result_scope) {
      payload.result_scope = destination.result_scope;
    }
    const response = await api.post(
      `/exports/prompt-run/${runId}`,
      Object.keys(payload).length > 0 ? payload : null,
      {
        headers: buildIdempotencyHeaders(
          resolveIdempotencyKey(idempotencyKey, `metadata-export-run:${runId}`),
        ),
      },
    );
    return response.data;
  },

  async getExports(limit: number = 200, indexId?: string, automationOnly?: boolean): Promise<MetadataExport[]> {
    const response = await api.get('/exports', { params: { limit, ...(indexId ? { index_id: indexId } : {}), ...(automationOnly ? { automation_only: true } : {}) } });
    return response.data;
  },

  async getExportStatus(exportId: string): Promise<MetadataExport> {
    const response = await api.get(`/exports/${exportId}`);
    return response.data;
  },

  async getWorkflowDelivery(sourceKind: WorkflowDelivery['source_kind'], sourceId: string): Promise<WorkflowDelivery> {
    const response = await api.get(`/workflow/deliveries/${sourceKind}/${encodeURIComponent(sourceId)}`);
    return response.data;
  },

  async downloadExport(exportId: string): Promise<void> {
    const transportBoundary = captureProtectedTransportBoundary();
    const response = await (async () => {
      try {
        const protectedResponse = await api.get(`/exports/${exportId}/download`, {
          responseType: 'blob',
          signal: transportBoundary.signal,
          // Metadata exports may legitimately exceed the ordinary API timeout.
          // The backend streams a generation-pinned object with a finite egress
          // reservation, so allow the browser enough time to receive that stream.
          timeout: 10 * 60 * 1000,
        });
        assertProtectedTransportBoundary(transportBoundary);
        return protectedResponse;
      } catch (error) {
        return rethrowProtectedTransportRequestError(transportBoundary, error);
      }
    })();
    const blob = response.data;
    if (!(blob instanceof Blob) || blob.size <= 0) {
      throw new Error('The metadata export download was empty or invalid.');
    }
    const contentLength = Number(response.headers['content-length']);
    if (
      Number.isFinite(contentLength)
      && contentLength >= 0
      && contentLength !== blob.size
    ) {
      throw new Error('The metadata export download was incomplete.');
    }
    const disposition = String(response.headers['content-disposition'] || '');
    const filenameMatch = disposition.match(/filename="([^"]+)"/i);
    const filename = filenameMatch?.[1] || `metadata-export-${exportId}.json`;
    const objectUrl = createProtectedObjectUrl(transportBoundary, blob);
    try {
      assertProtectedTransportBoundary(transportBoundary);
      triggerBrowserDownload(objectUrl, filename);
    } finally {
      window.setTimeout(() => releaseProtectedObjectUrl(objectUrl), 0);
    }
  },

  // Health check
  async healthCheck(): Promise<{ status: string; message: string }> {
    const response = await api.get('/health');
    return response.data;
  },

  // Playground APIs (simplified)
  async getPlaygroundVideos(limit: number = 50, cursor?: string, signal?: AbortSignal): Promise<PaginatedResponse<Video>> {
    const params: Record<string, any> = { limit };
    if (cursor) params.cursor = cursor;
    const response = await api.get('/playground/videos', { ...readConfig(params), signal });
    return response.data;
  },

  async getStudioSearchScope(videoIds?: string[], signal?: AbortSignal, containerIndexId = 'playground'): Promise<Omit<StudioSearchCatalog, 'videos'>> {
    const response = await api.post('/workflow/search/scope', {
      ...(videoIds ? { video_ids: videoIds } : {}), result_level: 'segment', latest_only: true, container_index_id: containerIndexId,
    }, { signal });
    return { ...response.data, fields: (response.data.fields || []).map((field: any) => ({
      ...field, itemType: field.itemType || undefined, description: field.description || undefined,
    })) };
  },

  async searchStudioVideos(options: { videoIds: string[]; query: string; filters: FilterCondition[]; signal?: AbortSignal; idempotencyKey?: string; containerIndexId?: string }): Promise<StudioSearchPage> {
    const response = await api.post('/workflow/search', {
      video_ids: options.videoIds, result_level: 'segment', limit: 50, latest_only: true, container_index_id: options.containerIndexId ?? 'playground',
      ...(options.query.trim() ? { query: options.query.trim() } : {}),
      ...(options.filters.length ? { filters: serializeFilterConditions(options.filters).map(({ type, ...condition }) => condition) } : {}),
    }, {
      signal: options.signal, timeout: SEARCH_REQUEST_TIMEOUT_MS,
      headers: buildIdempotencyHeaders(options.idempotencyKey || generateIdempotencyKey('studio-search')),
    });
    return { results: normalizeSearchResults(response.data.data), snapshotId: response.data.snapshot_id, nextCursor: response.data.pagination?.next_cursor || null,
      truncated: Boolean(response.data.pagination?.truncated), warnings: response.data.warnings || [] };
  },

  async getStudioSearchPage(snapshotId: string, cursor?: string, signal?: AbortSignal): Promise<StudioSearchPage> {
    const response = await api.get(`/workflow/searches/${encodeURIComponent(snapshotId)}/results`, {
      params: { limit: 50, ...(cursor ? { cursor } : {}) }, signal,
      timeout: SEARCH_REQUEST_TIMEOUT_MS,
    });
    return { results: normalizeSearchResults(response.data.data), snapshotId: response.data.snapshot_id, nextCursor: response.data.pagination?.next_cursor || null,
      truncated: Boolean(response.data.pagination?.truncated), warnings: response.data.warnings || [] };
  },

  // Batch operations
  async getBatchVideos(videoIds: string[]): Promise<VideoWithDetails[]> {
    const response = await api.post('/videos/batch', { video_ids: videoIds });
    return response.data;
  },

  async getBatchVideoSummaries(
    videoIds: string[],
    signal?: AbortSignal,
  ): Promise<Video[]> {
    const requestBody = {
      video_ids: videoIds,
      view: 'summary',
    };
    const response = signal
      ? await api.post('/videos/batch', requestBody, { signal })
      : await api.post('/videos/batch', requestBody);
    return response.data;
  },

  async getBatchVideoStatus(videoIds: string[]): Promise<VideoStatus[]> {
    const response = await api.post('/videos/batch/status', { video_ids: videoIds });
    return response.data;
  },

  async getBatchVideoSegments(
    targets: BatchVideoSegmentsTarget[],
    options?: { view?: 'gallery' },
  ): Promise<VideoSegmentsResponse[]> {
    const normalizedTargets = targets
      .map((target) => ({
        video_id: target.video_id,
        run_id: normalizeOptionalString(target.run_id) || undefined,
        segment_ids: Array.isArray(target.segment_ids)
          ? target.segment_ids.map(normalizeOptionalString).filter(Boolean) as string[]
          : undefined,
        cursor: normalizeOptionalString(target.cursor) || undefined,
        limit: Number.isInteger(target.limit) ? target.limit : undefined,
      }))
      .filter((target) => Boolean(target.video_id));
    const body: Record<string, any> = { targets: normalizedTargets };
    if (options?.view) body.view = options.view;
    const response = await api.post('/videos/batch/segments', body);
    return response.data;
  },

  // Markers (color tags + notes)
  async listMarkers(limit: number = 50, cursor?: string): Promise<PaginatedResponse<MarkerRecord>> {
    const params: Record<string, any> = { limit };
    if (cursor) params.cursor = cursor;
    const response = await api.get('/markers', { params });
    return response.data;
  },

  async upsertMarker(payload: UpsertMarkerRequest): Promise<MarkerRecord | null> {
    const response = await api.put('/markers', payload);
    return response.data;
  },

  async deleteMarker(markerId: string): Promise<{ message: string }> {
    const response = await api.delete(`/markers/${markerId}`);
    return response.data;
  },

  // Filter search APIs
  async filterSearch(
    indexId: string,
    conditions: FilterCondition[],
    runIds?: string[] | null,
    indexIds?: string[] | null,
    pageSize?: number,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<FilterSearchResponse> {
    const response = await api.post(`/search/filter/${indexId}`, {
      conditions: serializeFilterConditions(conditions),
      page_size: pageSize || 50,
      cursor,
      run_ids: runIds,
      index_ids: indexIds,
    }, {
      signal,
      timeout: SEARCH_REQUEST_TIMEOUT_MS,
    });
    return {
      results: normalizeSearchResults(response.data?.data),
      next_page_token: response.data?.pagination?.next_cursor || undefined,
      total_shown: Array.isArray(response.data?.data) ? response.data.data.length : 0,
    };
  },

  async getSqlSearchCatalog(
    indexId: string,
    options: {
      runIds?: string[] | null;
      indexIds?: string[] | null;
    } = {},
    signal?: AbortSignal,
  ): Promise<SqlSearchCatalogResponse> {
    const response = await api.post(
      `/search/sql/${indexId}/catalog`,
      {
        run_ids: options.runIds || undefined,
        index_ids: options.indexIds || undefined,
      },
      {
        signal,
        timeout: 15000,
      },
    );
    return {
      tables: Array.isArray(response.data?.tables) ? response.data.tables : [],
      limits: response.data?.limits || {
        max_loaded_rows: 50000,
        default_result_limit: 200,
        max_result_limit: 2000,
        query_timeout_seconds: 15,
      },
      index_ids: Array.isArray(response.data?.index_ids) ? response.data.index_ids : [],
      run_ids: Array.isArray(response.data?.run_ids) ? response.data.run_ids : [],
    };
  },

  async executeSqlSearch(
    indexId: string,
    query: string,
    options: {
      runIds?: string[] | null;
      indexIds?: string[] | null;
      resultLimit?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<SqlSearchExecuteResponse> {
    const response = await api.post(
      `/search/sql/${indexId}`,
      {
        query,
        run_ids: options.runIds || undefined,
        index_ids: options.indexIds || undefined,
        result_limit: options.resultLimit || undefined,
      },
      {
        signal,
        timeout: 30000,
      },
    );
    return {
      columns: Array.isArray(response.data?.columns) ? response.data.columns : [],
      rows: Array.isArray(response.data?.rows) ? response.data.rows : [],
      row_context: Array.isArray(response.data?.row_context) ? response.data.row_context : [],
      row_count: Number(response.data?.row_count || 0),
      truncated: Boolean(response.data?.truncated),
      applied_result_limit: Number(response.data?.applied_result_limit || 0),
      tables: Array.isArray(response.data?.tables) ? response.data.tables : [],
      index_ids: Array.isArray(response.data?.index_ids) ? response.data.index_ids : [],
      run_ids: Array.isArray(response.data?.run_ids) ? response.data.run_ids : [],
    };
  },

  async generateSqlSearchQuery(
    indexId: string,
    instruction: string,
    options: {
      existingQuery?: string | null;
      runIds?: string[] | null;
      indexIds?: string[] | null;
      idempotencyKey?: string;
    } = {},
    signal?: AbortSignal,
  ): Promise<SqlSearchGenerateResponse> {
    const idempotencyKey = resolveIdempotencyKey(options.idempotencyKey, 'sql-assistant');
    const response = await api.post(
      `/search/sql/${indexId}/generate`,
      {
        instruction,
        existing_query: options.existingQuery || undefined,
        run_ids: options.runIds || undefined,
        index_ids: options.indexIds || undefined,
      },
      {
        signal,
        timeout: 45000,
        headers: buildIdempotencyHeaders(idempotencyKey),
      },
    );
    return {
      query: typeof response.data?.query === 'string' ? response.data.query : '',
    };
  },
  // Chat APIs
  async createChatSession(data: CreateSessionRequest): Promise<ChatSession> {
    const response = await api.post('/chat/sessions', data);
    return normalizeAgentSessionSummary(response.data);
  },

  async getChatSessions(): Promise<ChatSession[]> {
    const response = await api.get('/chat/sessions');
    return Array.isArray(response.data)
      ? response.data.map(normalizeAgentSessionSummary)
      : [];
  },

  async getChatSession(
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<SessionHistoryResponse> {
    const response = signal
      ? await api.get(`/chat/sessions/${sessionId}`, { signal })
      : await api.get(`/chat/sessions/${sessionId}`);
    return normalizeAgentSessionDetail(response.data);
  },

  async deleteChatSession(sessionId: string): Promise<{
    session_id: string;
    status: 'draining' | 'deleted';
    message: string;
    retry_after_seconds?: number | null;
  }> {
    const response = await api.delete(`/chat/sessions/${sessionId}`);
    return response.data;
  },

  streamChatTurn(
    sessionId: string,
    message: SendMessageRequest,
    onMessage: (event: ChatStreamEvent) => void,
    onError?: (error: any) => void,
    onComplete?: () => void,
    idempotencyKey?: string,
    onOpen?: () => void,
  ): () => void {
    let controller: AbortController | null = new AbortController();
    let stopped = false;

    const dispatchChatEvent = (eventType: string, dataLines: string[]) => {
      if (!dataLines.length) return;
      const rawData = dataLines.join('\n');
      try {
        const parsed = JSON.parse(rawData);
        if (parsed && typeof parsed === 'object') {
          onMessage(normalizeChatStreamEvent(parsed));
          return;
        }
        if (eventType === 'error') {
          onMessage({ type: 'error', message: String(rawData) });
        }
      } catch (parseError) {
        console.error('Failed to parse chat SSE payload:', parseError);
      }
    };

    (async () => {
      try {
        const token = requireNonEmptyFirebaseBearerToken(
          await requireFirebaseTokenProvider()(),
        );
        if (stopped) {
          return;
        }
        const signal = controller?.signal;
        if (!signal) {
          return;
        }

        const sseUrl = `${API_BASE_URL}/chat/sessions/${sessionId}/turns/stream`;
        const resolvedIdempotencyKey = resolveIdempotencyKey(idempotencyKey, 'chat-turn');
        const response = await fetch(sseUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...buildIdempotencyHeaders(resolvedIdempotencyKey),
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...message,
            idempotency_key: resolvedIdempotencyKey,
          }),
          signal,
        });

        if (!response.ok) {
          const { data: parsedBody, message: parsedMessage } = await readFetchErrorPayload(response);
          throw createStreamRequestError(
            parsedMessage || `HTTP error while sending chat message: ${response.status}`,
            {
              status: response.status,
              retryable: isRetryableStreamRequestError({
                status: response.status,
                response: {
                  status: response.status,
                  data: parsedBody,
                },
              }),
              retryAfterMs: readFetchRetryAfterMs(response) ?? undefined,
              responseData: parsedBody,
            },
          );
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw createStreamRequestError('Chat stream has no response body', { retryable: true });
        }

        onOpen?.();

        const decoder = new TextDecoder();
        let buffer = '';
        let currentEventType = '';
        let currentDataLines: string[] = [];

        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const rawLine of lines) {
            const line = rawLine.replace(/\r$/, '');
            if (!line) {
              dispatchChatEvent(currentEventType, currentDataLines);
              currentEventType = '';
              currentDataLines = [];
              continue;
            }

            if (line.startsWith(':')) {
              continue;
            }

            const separatorIndex = line.indexOf(':');
            const field = separatorIndex >= 0 ? line.slice(0, separatorIndex) : line;
            let valueText = separatorIndex >= 0 ? line.slice(separatorIndex + 1) : '';
            if (valueText.startsWith(' ')) {
              valueText = valueText.slice(1);
            }

            if (field === 'event') {
              currentEventType = valueText;
            } else if (field === 'data') {
              currentDataLines.push(valueText);
            }
          }
        }

        if (buffer) {
          const finalLine = buffer.replace(/\r$/, '');
          if (finalLine.startsWith('event:')) {
            currentEventType = finalLine.slice(6).trim();
          } else if (finalLine.startsWith('data:')) {
            currentDataLines.push(finalLine.slice(5).trimStart());
          }
        }

        if (currentDataLines.length > 0) {
          dispatchChatEvent(currentEventType, currentDataLines);
        }

        if (!stopped) {
          onComplete?.();
        }
      } catch (error: any) {
        if (!stopped && error?.name !== 'AbortError' && onError) {
          onError(
            error && typeof error === 'object'
              ? error
              : createStreamRequestError('Chat stream interrupted.', { retryable: true }),
          );
        }
      }
    })();

    return () => {
      stopped = true;
      if (controller) {
        controller.abort();
        controller = null;
      }
    };
  },

  sendChatMessage(
    sessionId: string,
    message: SendMessageRequest,
    onMessage: (event: ChatStreamEvent) => void,
    onError?: (error: any) => void,
    onComplete?: () => void,
    idempotencyKey?: string,
    onOpen?: () => void,
  ): () => void {
    return apiService.streamChatTurn(sessionId, message, onMessage, onError, onComplete, idempotencyKey, onOpen);
  },

  subscribeProcessingProgress(
    scope: ProcessingProgressScope,
    onMessage: (message: ProgressMessage) => void,
    onConnection: (state: ProgressConnectionState) => void,
    onClosed: (reason: 'idle' | 'stopped' | 'error') => void,
  ): () => void {
    const boundary = captureProtectedTransportBoundary();
    return subscribeProgressTransport({
      url: `${API_BASE_URL}/processing/progress/stream`, scope, onMessage, onConnection, onClosed,
      getToken: async () => {
        const token = requireNonEmptyFirebaseBearerToken(await requireFirebaseTokenProvider()());
        assertProtectedTransportBoundary(boundary);
        return token;
      },
    });
  },

  async initiateResumableUpload(descriptor: LocalUploadDescriptor, title: string, indexId: string | null, key: string): Promise<ResumableUploadResponse> {
    const response = await api.post('/videos/uploads', { ...descriptor, title, index_id: normalizeUploadIndexId(indexId) || undefined }, {
      headers: buildIdempotencyHeaders(key),
    });
    return response.data;
  },

  async importMediaUrls(urls: string[], indexId: string | null, key: string): Promise<ImportJob> {
    notifyProcessingUploadIntent();
    return (await api.post('/workflow/attachments', {
      urls, index_id: normalizeUploadIndexId(indexId) || undefined,
    }, { headers: buildIdempotencyHeaders(key) })).data;
  },

  async uploadResumablePoster(jobId: string, poster: Blob, signal: AbortSignal): Promise<void> {
    await api.post(`/videos/uploads/${encodeURIComponent(jobId)}/poster`, poster, {
      headers: { 'Content-Type': 'image/webp' }, signal, timeout: UPLOAD_REQUEST_TIMEOUT_MS,
    });
  },

  async resumeResumableUpload(jobId: string, descriptor: LocalUploadDescriptor, restartExpired = false): Promise<ResumableUploadResponse> {
    return (await api.post(`/videos/uploads/${encodeURIComponent(jobId)}/resume`, { ...descriptor, restart_expired: restartExpired })).data;
  },

  async completeResumableUpload(jobId: string): Promise<ImportJob> {
    return (await api.post(`/videos/uploads/${encodeURIComponent(jobId)}/complete`, {})).data;
  },

  async initiateDriveUpload(fileId: string, token: string, indexId: string | null, title: string, key: string, resourceKey?: string): Promise<ResumableUploadResponse> {
    try {
      return (await api.post('/videos/uploads/google-drive', {
        file_id: fileId, resource_key: resourceKey, index_id: normalizeUploadIndexId(indexId) || undefined, title,
      }, { headers: { ...buildIdempotencyHeaders(key), [GOOGLE_DRIVE_ACCESS_TOKEN_HEADER]: token } })).data;
    } catch (error) { scrubGoogleDriveTokenFromTransport(error); throw error; }
  },

  async captureDriveUpload(jobId: string, token: string, resourceKey?: string, signal?: AbortSignal): Promise<ImportJob> {
    try {
      return (await api.post(`/videos/uploads/${encodeURIComponent(jobId)}/capture/google-drive`, { resource_key: resourceKey }, {
        headers: { [GOOGLE_DRIVE_ACCESS_TOKEN_HEADER]: token }, timeout: UPLOAD_REQUEST_TIMEOUT_MS, signal,
      })).data;
    } catch (error) { scrubGoogleDriveTokenFromTransport(error); throw error; }
  },

  // Cloud Connector APIs
  async getConnectors(): Promise<Connector[]> {
    const response = await api.get('/connectors');
    return response.data;
  },

  async getConnector(connectorId: string): Promise<Connector> {
    const response = await api.get(`/connectors/${connectorId}`);
    return response.data;
  },

  async createGCSConnector(
    data: CreateGCSConnectorRequest,
    credentialsFile: File,
    idempotencyKey?: string,
  ): Promise<Connector> {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('bucket', data.bucket);
    formData.append('gcp_project_id', data.gcp_project_id);
    const scopes = data.scopes && data.scopes.length > 0 ? data.scopes : ['import'];
    scopes.forEach((scope) => formData.append('scopes', scope));
    if (data.export_base_path) {
      formData.append('export_base_path', data.export_base_path);
    }
    if (data.import_mode) {
      formData.append('import_mode', data.import_mode);
    }
    formData.append('credentials_file', credentialsFile);

    const response = await api.post('/connectors/gcs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...buildIdempotencyHeaders(
          idempotencyKey || generateIdempotencyKey('connector-create-gcs'),
        ),
      },
    });
    return response.data;
  },

  async createS3Connector(
    data: CreateS3ConnectorRequest,
    idempotencyKey?: string,
  ): Promise<Connector> {
    const response = await api.post('/connectors/s3', data, {
      headers: buildIdempotencyHeaders(
        idempotencyKey || generateIdempotencyKey('connector-create-s3'),
      ),
    });
    return response.data;
  },

  async createAzureConnector(
    data: CreateAzureConnectorRequest,
    idempotencyKey?: string,
  ): Promise<Connector> {
    const response = await api.post('/connectors/azure', data, {
      headers: buildIdempotencyHeaders(
        idempotencyKey || generateIdempotencyKey('connector-create-azure'),
      ),
    });
    return response.data;
  },

  async updateConnector(connectorId: string, data: UpdateConnectorRequest): Promise<Connector> {
    const response = await api.patch(`/connectors/${connectorId}`, data);
    return response.data;
  },

  async testConnector(connectorId: string): Promise<TestConnectionResponse> {
    const response = await api.post(`/connectors/${connectorId}/test`);
    return response.data;
  },

  async deleteConnector(connectorId: string): Promise<{ message: string }> {
    const response = await api.delete(`/connectors/${connectorId}`);
    return response.data;
  },

  async browseConnectorFiles(connectorId: string, data: BrowseFilesRequest): Promise<CloudFile[]> {
    const response = await api.post(`/connectors/${connectorId}/browse`, data);
    return response.data;
  },

  // Import Job APIs
  async createImportJob(data: CreateImportJobRequest, idempotencyKey?: string): Promise<ImportJob> {
    notifyProcessingUploadIntent();
    const response = await api.post('/import-jobs', data, {
      headers: buildIdempotencyHeaders(
        resolveIdempotencyKey(idempotencyKey, 'import-job-create'),
      ),
    });
    return response.data;
  },

  async getImportJobs(status?: string, indexId?: string, limit?: number, automationOnly?: boolean): Promise<ImportJob[]> {
    const params = { ...(status ? { status } : {}), ...(indexId ? { index_id: indexId } : {}), ...(limit ? { limit } : {}), ...(automationOnly ? { automation_only: true } : {}) };
    const response = await api.get('/import-jobs', { params });
    return response.data;
  },

  async getImportJob(jobId: string): Promise<ImportJob> {
    const response = await api.get(`/import-jobs/${jobId}`);
    return response.data;
  },

  async getImportJobOutputs(jobId: string, limit = 100, cursor?: string): Promise<ImportJobOutputsPage> {
    const response = await api.get(`/import-jobs/${encodeURIComponent(jobId)}/outputs`, {
      params: { limit, ...(cursor ? { cursor } : {}) },
    });
    return response.data;
  },

  async getImportJobFiles(jobId: string, limit = 100, cursor?: string, failedOnly = false): Promise<ImportJobFilesPage> {
    const response = await api.get(`/import-jobs/${encodeURIComponent(jobId)}/files`, {
      params: { limit, failed_only: failedOnly, ...(cursor ? { cursor } : {}) },
    });
    return response.data;
  },

  async getAllImportJobOutputs(jobId: string): Promise<ImportJobOutput[]> {
    const outputs: ImportJobOutput[] = [];
    let cursor: string | undefined;
    do {
      const page = await this.getImportJobOutputs(jobId, 100, cursor);
      outputs.push(...page.items);
      cursor = page.next_cursor || undefined;
    } while (cursor);
    return outputs;
  },

  /** The durable job remains visible in Jobs when its initiating view closes. */
  async waitForImportJob(job: ImportJob, onProgress: (job: ImportJob) => void, isCurrent: () => boolean = () => true): Promise<Video[]> {
    const boundary = captureProtectedTransportBoundary();
    if (!isCurrent()) throw new Error(`Import ${job.job_id} continues in Jobs.`);
    const { processingRealtimeStore } = await import('./processingRealtimeStore');
    onProgress(job);
    const current = ['completed', 'failed', 'cancelled'].includes(job.status) ? job : await new Promise<ImportJob>((resolve, reject) => {
      let unsubscribe = () => {};
      const abort = () => { unsubscribe(); reject(new DOMException('Observation ended.', 'AbortError')); };
      const finish = (next: ImportJob) => {
        onProgress(next);
        if (['completed', 'failed', 'cancelled'].includes(next.status)) {
          unsubscribe(); boundary.signal.removeEventListener('abort', abort); resolve(next);
        }
      };
      unsubscribe = processingRealtimeStore.subscribe((event) => {
        if (event.event_type !== 'import_job.progress' || event.payload?.job_id !== job.job_id) return;
        finish({ ...job, ...event.payload.snapshot });
      }, { import_job_ids: [job.job_id] });
      boundary.signal.addEventListener('abort', abort, { once: true });
      if (boundary.signal.aborted) abort();
      const cached = processingRealtimeStore.getImportJob(job.job_id);
      if (cached) finish({ ...job, ...cached });
    });
    assertProtectedTransportBoundary(boundary);
    if (current.status !== 'completed') throw new Error(current.error_message || `Import ${current.status}. Open Jobs to inspect or retry ${job.job_id}.`);
    const outputs = await this.getAllImportJobOutputs(job.job_id);
    const ids = outputs.flatMap((part) => part.video_id ? [part.video_id] : []);
    const videos: Video[] = [];
    for (let offset = 0; offset < ids.length; offset += 100) {
      assertProtectedTransportBoundary(boundary);
      if (!isCurrent()) throw new Error(`Import ${job.job_id} is available in Jobs.`);
      videos.push(...await this.getBatchVideoSummaries(ids.slice(offset, offset + 100), boundary.signal));
    }
    assertProtectedTransportBoundary(boundary);
    const byId = new Map(videos.map((video) => [video.video_id, video]));
    return ids.flatMap((id) => byId.has(id) ? [byId.get(id)!] : []);
  },

  async retryImportJob(
    jobId: string,
    data?: RetryImportJobRequest,
    idempotencyKey?: string,
  ): Promise<AttachmentImportJobResponse> {
    notifyProcessingUploadIntent();
    const response = await api.post(`/import-jobs/${jobId}/retry`, data, {
      headers: buildIdempotencyHeaders(
        resolveIdempotencyKey(idempotencyKey, `import-job-retry:${jobId}`),
      ),
    });
    return response.data;
  },

  async cancelImportJob(jobId: string): Promise<ImportJob> {
    const response = await api.post(`/import-jobs/${jobId}/cancel`);
    return response.data;
  },

  async getImportJobsByConnector(connectorId: string): Promise<ImportJob[]> {
    const jobs = await this.getImportJobs();
    const filtered = jobs.filter(job => job.connector_id === connectorId);
    return filtered;
  },

  async getImportAutomation(indexId: string): Promise<ImportAutomationConfig | null> {
    const response = await api.get(`/indexes/${indexId}/automations/import`);
    return response.data;
  },

  async putImportAutomation(
    indexId: string,
    data: PutImportAutomationRequest,
    idempotencyKey?: string,
  ): Promise<ImportAutomationConfig> {
    const operationKey = resolveIdempotencyKey(
      idempotencyKey,
      `automation-import-save:${indexId}`,
    );
    const response = await api.put(
      `/indexes/${indexId}/automations/import`,
      data,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  async pauseImportAutomation(
    indexId: string,
    idempotencyKey?: string,
  ): Promise<ImportAutomationConfig | null> {
    const operationKey = resolveIdempotencyKey(
      idempotencyKey,
      `automation-import-pause:${indexId}`,
    );
    const response = await api.post(
      `/indexes/${indexId}/automations/import/pause`,
      undefined,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  async resumeImportAutomation(
    indexId: string,
    idempotencyKey?: string,
  ): Promise<ImportAutomationConfig | null> {
    const operationKey = resolveIdempotencyKey(
      idempotencyKey,
      `automation-import-resume:${indexId}`,
    );
    const response = await api.post(
      `/indexes/${indexId}/automations/import/resume`,
      undefined,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  async getExportAutomation(indexId: string): Promise<ExportAutomationConfig | null> {
    const response = await api.get(`/indexes/${indexId}/automations/export`);
    return response.data;
  },

  async putExportAutomation(
    indexId: string,
    data: PutExportAutomationRequest,
    idempotencyKey?: string,
  ): Promise<ExportAutomationConfig> {
    const operationKey = resolveIdempotencyKey(
      idempotencyKey,
      `automation-export-save:${indexId}`,
    );
    const response = await api.put(
      `/indexes/${indexId}/automations/export`,
      data,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  async pauseExportAutomation(
    indexId: string,
    idempotencyKey?: string,
  ): Promise<ExportAutomationConfig | null> {
    const operationKey = resolveIdempotencyKey(
      idempotencyKey,
      `automation-export-pause:${indexId}`,
    );
    const response = await api.post(
      `/indexes/${indexId}/automations/export/pause`,
      undefined,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  async resumeExportAutomation(
    indexId: string,
    idempotencyKey?: string,
  ): Promise<ExportAutomationConfig | null> {
    const operationKey = resolveIdempotencyKey(
      idempotencyKey,
      `automation-export-resume:${indexId}`,
    );
    const response = await api.post(
      `/indexes/${indexId}/automations/export/resume`,
      undefined,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  // Webhook APIs
  async getWebhooks(): Promise<Webhook[]> {
    const response = await api.get('/webhooks');
    return response.data;
  },

  async getWebhook(webhookId: string): Promise<Webhook> {
    const response = await api.get(`/webhooks/${webhookId}`);
    return response.data;
  },

  async createWebhook(
    data: CreateWebhookRequest,
    idempotencyKey?: string,
  ): Promise<WebhookCreateResponse> {
    const operationKey = resolveIdempotencyKey(idempotencyKey, 'webhook-create');
    const response = await api.post(
      '/webhooks',
      data,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  async updateWebhook(webhookId: string, data: UpdateWebhookRequest): Promise<Webhook> {
    const response = await api.patch(`/webhooks/${webhookId}`, data);
    return response.data;
  },

  async deleteWebhook(webhookId: string): Promise<{ message: string }> {
    const response = await api.delete(`/webhooks/${webhookId}`);
    return response.data;
  },

  async testWebhook(webhookId: string): Promise<TestWebhookResponse> {
    const response = await api.post(`/webhooks/${webhookId}/test`);
    return response.data;
  },

  async rotateWebhookSecret(
    webhookId: string,
    idempotencyKey?: string,
  ): Promise<RotateSecretResponse> {
    const operationKey = resolveIdempotencyKey(
      idempotencyKey,
      `webhook-secret-rotate:${webhookId}`,
    );
    const response = await api.post(
      `/webhooks/${webhookId}/rotate-secret`,
      undefined,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  async getWebhookDeliveries(webhookId: string, status?: string, limit: number = 50): Promise<WebhookDelivery[]> {
    const params: any = { limit };
    if (status) params.status = status;
    const response = await api.get(`/webhooks/${webhookId}/deliveries`, { params });
    return response.data;
  },

  async getWebhookDelivery(deliveryId: string): Promise<WebhookDelivery> {
    const response = await api.get(`/webhooks/deliveries/${deliveryId}`);
    return response.data;
  },

  async retryWebhookDelivery(deliveryId: string): Promise<WebhookDelivery> {
    const response = await api.post(`/webhooks/deliveries/${deliveryId}/retry`);
    return response.data;
  },

  async getWebhookEvents(): Promise<string[]> {
    const response = await api.get('/webhooks/events');
    return response.data;
  },

  // API Key Management APIs
  async getApiKeys(): Promise<ApiKey[]> {
    const response = await api.get('/api-keys');
    return response.data;
  },

  async getApiKey(keyId: string): Promise<ApiKey> {
    const response = await api.get(`/api-keys/${keyId}`);
    return response.data;
  },

  async createApiKey(
    data: CreateApiKeyRequest,
    idempotencyKey?: string,
  ): Promise<ApiKeyCreateResponse> {
    const operationKey = resolveIdempotencyKey(idempotencyKey, 'api-key-create');
    const response = await api.post(
      '/api-keys',
      data,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  async deleteApiKey(keyId: string): Promise<{ message: string }> {
    const response = await api.delete(`/api-keys/${keyId}`);
    return response.data;
  },

  async revokeApiKey(keyId: string): Promise<ApiKey> {
    const response = await api.post(`/api-keys/${keyId}/revoke`);
    return response.data;
  },

  async rotateApiKey(
    keyId: string,
    idempotencyKey?: string,
  ): Promise<ApiKeyRotateResponse> {
    const operationKey = resolveIdempotencyKey(
      idempotencyKey,
      `api-key-rotate:${keyId}`,
    );
    const response = await api.post(
      `/api-keys/${keyId}/rotate`,
      undefined,
      { headers: buildIdempotencyHeaders(operationKey) },
    );
    return response.data;
  },

  // Usage Tracking APIs
  async getCurrentUsage(): Promise<CurrentUsageResponse> {
    const response = await api.get('/usage');
    return response.data;
  },

  async getUsageHistory(months: number = 6): Promise<UsageHistoryResponse> {
    const response = await api.get('/usage/history', { params: { limit: months } });
    return response.data;
  },

  async getUsageDetails(
    metricType?: string,
    startDate?: string,
    endDate?: string,
    limit: number = 100
  ): Promise<UsageEvent[]> {
    const params: any = { limit };
    if (metricType) params.metric_type = metricType;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/usage/details', { params });
    return response.data;
  },

  async getUsageBreakdown(startDate?: string, endDate?: string): Promise<UsageBreakdownResponse> {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/usage/breakdown', { params });
    return response.data;
  },

  async getMetricTypes(): Promise<MetricTypeInfo[]> {
    const response = await api.get('/usage/metric-types');
    return response.data;
  },

  // MCP (Model Context Protocol) APIs
  async getMcpStatus(): Promise<MCPStatusResponse> {
    const response = await api.get('/mcp/status');
    return response.data;
  },

  async getMcpConfig(platform: MCPPlatform = 'claude-desktop', apiKeyId?: string): Promise<MCPConfigResponse> {
    const params: any = { platform };
    if (apiKeyId) params.api_key_id = apiKeyId;
    const response = await api.get('/mcp/config', { params });
    return response.data;
  },

  async getMcpTools(): Promise<MCPToolsResponse> {
    const response = await api.get('/mcp/tools');
    return response.data;
  },

  async executeMcpPlayground(
    toolName: string,
    args: Record<string, any>,
    idempotencyKey?: string,
  ): Promise<MCPPlaygroundResponse> {
    const explicitArgumentKey = typeof args.idempotency_key === 'string'
      ? args.idempotency_key.trim()
      : '';
    const retainedKey = idempotencyKey?.trim() || '';
    if (explicitArgumentKey && retainedKey && explicitArgumentKey !== retainedKey) {
      throw new Error(
        'idempotency_key must match the retained playground attempt key',
      );
    }
    const requestKey = explicitArgumentKey || retainedKey;
    const requestConfig = requestKey
      ? { headers: buildIdempotencyHeaders(requestKey) }
      : undefined;
    const response = await api.post('/mcp/playground', {
      tool_name: toolName,
      arguments: args
    }, requestConfig);
    return response.data;
  },

  async uploadMcpAttachment(grant: MCPAttachmentUploadGrant, file: File, fileId: string): Promise<void> {
    const boundary = captureProtectedTransportBoundary();
    const descriptor = await describeUploadFile(file);
    assertProtectedTransportBoundary(boundary);
    notifyProcessingUploadIntent(boundary.userId);
    const response = await axios.post<{ job: ImportJob; upload: ResumableUploadSession | null }>(grant.url,
      { file_id: fileId, ...descriptor }, {
        headers: { Authorization: `Bearer ${grant.token}` }, timeout: UPLOAD_REQUEST_TIMEOUT_MS, signal: boundary.signal,
      });
    assertProtectedTransportBoundary(boundary);
    if (response.data.upload !== null) await transferResumableFile(file, response.data.upload, boundary.signal);
    assertProtectedTransportBoundary(boundary);
    await axios.post(`${grant.url}/complete`, { file_id: fileId }, {
      headers: { Authorization: `Bearer ${grant.token}` }, timeout: UPLOAD_REQUEST_TIMEOUT_MS, signal: boundary.signal,
    });
    assertProtectedTransportBoundary(boundary);
  },

  async completeMcpAttachments(grant: MCPAttachmentUploadGrant, fileIds: string[]): Promise<Record<string, any>> {
    const boundary = captureProtectedTransportBoundary();
    const response = await axios.post(grant.complete_url, { file_ids: fileIds }, {
      headers: { Authorization: `Bearer ${grant.token}` },
      signal: boundary.signal,
      timeout: SEARCH_REQUEST_TIMEOUT_MS,
    });
    assertProtectedTransportBoundary(boundary);
    return response.data;
  },

  // Billing APIs
  async getBillingAccount(options: AccountBootstrapRequestOptions = {}): Promise<BillingAccount> {
    const requestConfig = options.includeAffiliateReferral
      ? await prepareAffiliateBootstrapRequestConfig()
      : undefined;
    const response = requestConfig
      ? await api.get('/billing/account', requestConfig)
      : await api.get('/billing/account');
    return response.data;
  },

  async getBillingTransactions(
    limit: number = 50,
    offset: number = 0,
    transactionType?: string,
    transactionTypes?: readonly string[],
  ): Promise<TransactionListResponse> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (transactionType) {
      params.set('transaction_type', transactionType);
    }
    transactionTypes?.forEach((type) => params.append('transaction_types', type));
    const response = await api.get('/billing/transactions', {
      params
    });
    return response.data;
  },

  async getCreditPackages(): Promise<CreditPackage[]> {
    const response = await api.get('/billing/packages');
    return response.data;
  },

  async estimateCost(operationType: string, params: Record<string, any>): Promise<CostEstimate> {
    const response = await api.post('/billing/estimate', {
      operation_type: operationType,
      params
    });
    return response.data;
  },

  async createCheckoutSession(packageId: string, successUrl?: string, cancelUrl?: string): Promise<CheckoutSessionResponse> {
    const idempotencyKey = resolveIdempotencyKey(undefined, `billing-purchase:${packageId}`);
    const response = await api.post(
      '/billing/purchase',
      {
        package_id: packageId,
        success_url: successUrl,
        cancel_url: cancelUrl
      },
      { headers: buildIdempotencyHeaders(idempotencyKey) }
    );
    return response.data;
  },

  async createCustomCheckoutSession(mtAmount: number, successUrl?: string, cancelUrl?: string): Promise<CheckoutSessionResponse> {
    const idempotencyKey = resolveIdempotencyKey(undefined, `billing-custom:${mtAmount}`);
    const response = await api.post(
      '/billing/purchase/custom',
      {
        mt_amount: mtAmount,
        success_url: successUrl,
        cancel_url: cancelUrl,
      },
      { headers: buildIdempotencyHeaders(idempotencyKey) }
    );
    return response.data;
  },

  async getBillingPortalUrl(returnUrl?: string): Promise<BillingPortalResponse> {
    const response = await api.post('/billing/portal', {
      return_url: returnUrl
    });
    return response.data;
  },

  async getBillingPricingConfig(): Promise<BillingPricingConfig> {
    const response = await api.get('/billing/pricing');
    return response.data;
  },

  async getUsageDeductions(limit: number = 50): Promise<DeductionListResponse> {
    const response = await api.get('/billing/deductions', {
      params: { limit }
    });
    return response.data;
  },

  async getBillingBudgetAlerts(): Promise<BillingBudgetAlertsResponse> {
    const response = await api.get('/billing/budget-alerts');
    return response.data;
  },

  async updateBillingBudgetAlerts(request: BillingBudgetAlertsRequest): Promise<BillingBudgetAlertsResponse> {
    const response = await api.put('/billing/budget-alerts', request);
    return response.data;
  },

  // Plans and subscription management
  async getPlans(): Promise<Plan[]> {
    const response = await api.get('/plans');
    return response.data;
  },

  async checkoutPlan(planId: string, successUrl?: string, cancelUrl?: string): Promise<PlanCheckoutResponse> {
    const idempotencyKey = resolveIdempotencyKey(undefined, `plan-checkout:${planId}`);
    const response = await api.post(
      '/plans/checkout',
      {
        plan_id: planId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      },
      { headers: buildIdempotencyHeaders(idempotencyKey) }
    );
    return response.data;
  },

  async selectPlan(planId: string): Promise<SelectPlanResponse> {
    const idempotencyKey = resolveIdempotencyKey(
      undefined,
      `plan-select:${planId}`
    );
    const response = await api.post(
      '/plans/select',
      {
        plan_id: planId,
      },
      { headers: buildIdempotencyHeaders(idempotencyKey) }
    );
    return response.data;
  },

  async getPlanCheckoutStatus(sessionId: string): Promise<PlanCheckoutStatusResponse> {
    const response = await api.get('/plans/checkout/status', {
      params: { session_id: sessionId },
    });
    return response.data;
  },

  // User Profile APIs
  async recordProcessingActivity(
    data: ProcessingActivityRequest,
    expectedUserId: string,
    signal?: AbortSignal,
  ): Promise<ProcessingActivityResponse> {
    const boundary = captureProtectedTransportBoundary();
    if (boundary.userId !== expectedUserId) throw createAuthBoundaryUnsettledError();
    const controller = new AbortController();
    const abort = () => controller.abort();
    boundary.signal.addEventListener('abort', abort);
    signal?.addEventListener('abort', abort);
    if (boundary.signal.aborted || signal?.aborted) abort();
    try {
      const response = await api.post('/users/me/processing-activity', data, {
        signal: controller.signal,
        timeout: 5000,
      });
      assertProtectedTransportBoundary(boundary);
      return response.data;
    } finally {
      boundary.signal.removeEventListener('abort', abort);
      signal?.removeEventListener('abort', abort);
    }
  },

  async requestEmailVerification(idempotencyKey: string): Promise<void> {
    await api.post('/auth/email-verification', {}, {
      headers: buildIdempotencyHeaders(idempotencyKey),
    });
  },

  async getUserProfile(options: AccountBootstrapRequestOptions = {}): Promise<UserProfile> {
    const requestConfig = options.includeAffiliateReferral
      ? await prepareAffiliateBootstrapRequestConfig()
      : undefined;
    const response = requestConfig
      ? await api.get('/users/me', requestConfig)
      : await api.get('/users/me');
    return response.data;
  },

  async updateUserProfile(data: UpdateProfileRequest): Promise<UserProfile> {
    const response = await api.patch('/users/me', data);
    return response.data;
  },

  async getUserSettings(): Promise<UserSettings> {
    const response = await api.get('/users/me/settings');
    return response.data;
  },

  async updateUserSettings(data: UpdateSettingsRequest): Promise<UserSettings> {
    const response = await api.patch('/users/me/settings', data);
    return response.data;
  },

  async completeWorkOSOAuth(externalAuthId: string): Promise<WorkOSOAuthCompletionResponse> {
    if (
      !externalAuthId
      || externalAuthId.length > 255
      || !/^[\x21-\x7e]+$/.test(externalAuthId)
    ) {
      throw new Error('A valid WorkOS external authentication identifier is required');
    }
    const response = await api.post('/oauth/workos/complete', {
      external_auth_id: externalAuthId,
    });
    return response.data;
  },

  // Feedback methods
  async submitFeedback(data: SubmitFeedbackRequest): Promise<SubmitFeedbackResponse> {
    const response = await api.post('/users/me/feedback', data);
    return response.data;
  },

  async listFeedback(limit: number = 20, cursor?: string): Promise<FeedbackListResponse> {
    const params: Record<string, any> = { limit };
    if (cursor) params.cursor = cursor;
    const response = await api.get('/users/me/feedback', { params });
    return response.data;
  }
};

export default apiService;
